import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import {
  Package, ChevronRight, ChevronLeft, Check, Layers, Settings2,
  FileCheck, Rocket, AlertCircle, ShieldCheck, Ticket,
  RefreshCw, ChevronDown, Cpu, Github, Cloud, Network, Sparkles,
} from 'lucide-react'
import clsx from 'clsx'
import { useProducts, useBundles, useSizeConfigs } from '@/hooks/useProducts'
import { useUserRoles } from '@/contexts/UserRolesContext'
import { useCreateDeployment, useTriggerDeployment } from '@/hooks/useDeployments'
import { useAzurePricing, useRossDeploymentCost, useBacpacFiles } from '@/hooks/usePricing'
import { useDeploymentWizard } from '@/stores/deploymentWizard'
import { deploymentsApi } from '@/services/deployments'
import { Button } from '@/components/common/Button'
import { SizeBadge } from '@/components/common/SizeBadge'
import { PoolTierSelector } from '@/components/common/PoolTierSelector'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { EmptyState } from '@/components/common/EmptyState'
import { formatApiError, formatCurrency } from '@/utils/format'
import { isAppriseProduct } from '@/utils/apprise'
import { useAppriseVmForm } from '@/components/apprise-vm/useAppriseVmForm'
import { AppriseServerSection } from '@/components/apprise-vm/AppriseServerSection'
import { AppriseReviewSection, AppriseCostSummary } from '@/components/apprise-vm/AppriseReviewSection'
import { AppriseSizeDetails, AppriseUnverifiedNotice } from '@/components/apprise-vm/AppriseSizeCard'
import { catalogVmSizing } from '@/components/apprise-vm/appriseDefaults'
import { osDiskLabel } from '@/components/apprise-vm/appriseReview'
import { WORKWISE_ONLY_REGIONS, WORKWISE_REGIONS } from '@/config/azureRegions'
import { useWorkwiseSelection } from '@/hooks/useWorkwiseSelection'
import { useWorkwiseFlow } from '@/hooks/useWorkwiseFlow'
import { WORKWISE_ENVIRONMENTS } from '@/config/workwise'
import { WorkwiseSelectionNotice } from '@/components/workwise/WorkwiseSelectionNotice'
import { WorkwiseSizeGrid } from '@/components/workwise/WorkwiseSizeGrid'
import { WorkwiseCustomerSection, WorkwiseInfraSections } from '@/components/workwise/WorkwiseInfra'
import { WorkwiseAppSettings } from '@/components/workwise/WorkwiseAppSettings'
import { stepAfter, stepBefore } from '@/utils/workwise/serverErrors'
import { WorkwiseCostSummary, WorkwiseReviewSections } from '@/components/workwise/WorkwiseReview'
import {
  DEFAULT_PRODUCT_LINE_ID,
  PRODUCT_LINES,
  infraProductForLine,
  isProductLineId,
  isTraverse,
  pipelineValueOf,
  productLineLabel,
  productLineOf,
  type ProductLineId,
} from '@/data/productLines'
import { T_SHIRT_SIZES as SIZES, T_SHIRT_SIZE_LABELS as SIZE_LABELS } from '@/data/tshirtSizes'
import { getPoolTier, tierHeadline } from '@/data/sizeCatalog'
import { createsNewSqlVm, sizingForDeployment, usesPoolTiers } from '@/utils/sizing'
import {
  deriveCustomerDbNames,
  normalizeCustomerId,
  validateCustomerId,
} from '@/utils/traverseProduction'
import { isTShirtSize, type ProductSize, type TShirtSize, type Environment, type DeploymentStrategy, type PipelineType, type Product, type CloudProvider } from '@/types'
import { getRossRegionResources } from '@/data/rossRegionResources'
import { ENVIRONMENTS, PROD_ENVS } from '@/data/environments'
import type { InfraProduct } from '@/types/infrastructure'
import { usePublicIpQuota } from '@/hooks/usePublicIpQuota'
import { useVmSizeCheck } from '@/hooks/useVmSizeCheck'
import { InfrastructureFlow } from '@/components/infrastructure/InfrastructureFlow'
import { InfrastructureReview } from '@/components/infrastructure/InfrastructureReview'
import { VmSizeBanner } from '@/components/infrastructure/VmSizeBanner'
import { ProductChip } from '@/components/infrastructure/ProductChip'
import { SizingCostSummary } from '@/components/infrastructure/SizingCostSummary'
import { SizesYouChose } from '@/components/infrastructure/SizesYouChose'
import { rdsPublicIp, rdsVmCount, useInfrastructureWizard } from '@/stores/infrastructureWizard'
import { buildInfrastructurePayload, legacyFields } from '@/utils/infraPayload'
import { asksLicense, validateInfrastructure } from '@/utils/infraValidation'
import { requiresCustomerCode } from '@/utils/naming'
import { CUSTOMER_CODE_LABEL } from '@/data/customerCode'
import { ApplicationSettings } from '@/components/infrastructure/ApplicationSettings'
import { applicationInstallPlan, applicationProblems } from '@/utils/applicationInstall'
import { applicationAnswersFrom, buildApplicationConfig, summariseApplication, type ApplicationContext } from '@/utils/applicationDetails'
import type { ApplicationAnswers } from '@/types/application'
import { useServerNameRecheck } from '@/hooks/useServerNameRecheck'
import styles from './NewDeploymentPage.module.css'

const STEPS = [
  { number: 1, label: 'Choose Products',           icon: Layers },
  { number: 2, label: 'Configure Sizes',           icon: Settings2 },
  { number: 3, label: 'Infrastructure & Settings', icon: Network },
  { number: 4, label: 'Application (ERP)',         icon: Package },
  { number: 5, label: 'Review & Deploy',           icon: FileCheck },
]

/** Customer Name has no length limit here. The pipeline (infrastructure-building-blocks, variables.tf) still allows 7 characters. */
const customerNameProblem = (name: string): string | null => (name.trim() === '' ? 'Customer Name is required.' : null)




const IBM_REGIONS: Array<{ value: string; label: string }> = [
  { value: 'us-south',    label: 'US South (Dallas)' },
  { value: 'us-east',     label: 'US East (Washington DC)' },
  { value: 'eu-gb',       label: 'UK South (London)' },
  { value: 'eu-de',       label: 'EU Central (Frankfurt)' },
  { value: 'jp-tok',      label: 'Asia Pacific (Tokyo)' },
  { value: 'jp-osa',      label: 'Asia Pacific (Osaka)' },
  { value: 'au-syd',      label: 'Asia Pacific (Sydney)' },
  { value: 'ca-tor',      label: 'Canada (Toronto)' },
  { value: 'br-sao',      label: 'South America (São Paulo)' },
]

const CLOUD_PROVIDERS: Array<{ value: CloudProvider; label: string; description: string }> = [
  { value: 'azure', label: 'Microsoft Azure', description: 'AKS · Azure VNet · Azure PostgreSQL' },
  { value: 'ibm',   label: 'IBM Cloud',       description: 'IKS · IBM VPC · IBM Databases' },
]


const STRATEGY_LABELS: Record<DeploymentStrategy, string> = {
  rolling: 'Rolling Update', blue_green: 'Blue / Green', canary: 'Canary', recreate: 'Recreate',
}

const BLUE_GREEN_FAMILIES = new Set(['appcentral', 'aip'])

// Ross ERP app-deploy (Azure DevOps pipeline 1522) option lists
const IAF_VERSIONS = ['IAF9.3-SP12', 'IAF9.3-SP13', 'IAF9.3-SP14', 'IAF9.3-SP15', 'IAF10.0']

// ─── Schemas ──────────────────────────────────────────────────────────────────

const infraSchema = z.object({
  pipeline_type: z.enum(['github_actions', 'azure_devops']),
})
type InfraFormValues = z.infer<typeof infraSchema>

const envSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  description: z.string().optional(),
  customer_name: z.string().min(1, 'Customer name is required'),
  tenant_id: z.string().min(1, 'Tenant ID is required'),
  cloud_provider: z.enum(['azure', 'ibm']),
  environment: z.enum(['dev', 'stg', 'qa', 'uat', 'demo', 'production', 'uat_usgov', 'prod_usgov']),
  // Azure
  azure_region: z.string().optional(),
  secondary_region: z.string().optional(),
  // IBM Cloud
  ibm_region: z.string().optional(),
  ibm_region_secondary: z.string().optional(),
  ibm_resource_group: z.string().optional(),
  ibm_account_id: z.string().optional(),
  ibm_vpc_name: z.string().optional(),
  strategy: z.enum(['rolling', 'blue_green', 'canary', 'recreate']),
})
type EnvFormValues = z.infer<typeof envSchema>

// ─── Simple Toggle Component ──────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      style={disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
      className={clsx(styles.toggle, checked && styles.toggleOn)}
      onClick={() => { if (!disabled) onChange(!checked) }}
    >
      <span className={styles.toggleThumb} />
    </button>
  )
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function ApprovalModal({
  onApprove,
  onDirect,
  onCancel,
  isProd,
}: {
  onApprove: (notes: string) => void
  onDirect: () => void
  onCancel: () => void
  isProd: boolean
}) {
  const [notes, setNotes] = useState('')
  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className={styles.modalIcon}>
          <ShieldCheck size={28} color="var(--aptean-teal)" />
        </div>
        <h2 className="modal-title">Deployment Approval</h2>
        <p className="modal-subtitle">
          {isProd
            ? 'Production deployments require manager approval before execution.'
            : 'You can request approval or deploy directly.'}
        </p>
        <div className={styles.formGroup}>
          <label className={styles.label}>Notes / Justification</label>
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder="Describe the purpose and impact of this deployment..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          {!isProd && (
            <Button variant="secondary" onClick={onDirect}>
              Deploy Directly
            </Button>
          )}
          <Button icon={<ShieldCheck size={14} />} onClick={() => onApprove(notes)}>
            Submit for Approval
          </Button>
        </div>
      </div>
    </div>
  )
}

function ServiceDeskModal({
  deploymentName,
  onSubmit,
  onSkip,
}: {
  deploymentName: string
  onSubmit: (data: { title: string; description: string; priority: string }) => void
  onSkip: () => void
}) {
  const [title, setTitle] = useState(`Deploy: ${deploymentName}`)
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  return (
    <div className="modal-overlay">
      <div className="modal-box modal-box-lg">
        <div className={styles.modalIcon}>
          <Ticket size={28} color="var(--aptean-teal)" />
        </div>
        <h2 className="modal-title">Create Service Desk Ticket</h2>
        <p className="modal-subtitle">
          Optionally create a change-management ticket for this deployment.
        </p>
        <div className={styles.formGrid2}>
          <div className={styles.formGroup} style={{ gridColumn: '1/-1' }}>
            <label className={styles.label}>Title</label>
            <input
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ticket title"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Priority</label>
            <select className={styles.select} value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className={styles.formGroup} style={{ gridColumn: '1/-1' }}>
            <label className={styles.label}>Description</label>
            <textarea
              className={styles.textarea}
              rows={3}
              placeholder="Additional details about this change..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onSkip}>Skip</Button>
          <Button icon={<Ticket size={14} />} onClick={() => onSubmit({ title, description, priority })}>
            Create Ticket &amp; Deploy
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function NewDeploymentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectedProductId = searchParams.get('product')
  const preselectedProvider = searchParams.get('provider') as CloudProvider | null

  const { data: products, isLoading: productsLoading } = useProducts()
  const { data: bundles } = useBundles()
  const { data: sizeConfigs } = useSizeConfigs()
  const { canDeploy, isAdmin, isDeployer, loading: rolesLoading, initialized: rolesInitialized } = useUserRoles()


  // Fetch ERP versions from admin API; fall back to hardcoded list
  const ERP_VERSION_FALLBACK = ['ERP71SP4', 'ERP80GA', 'ERP80SP1', 'ERP80SP2', 'ERP81GA']
  const { data: erpVersionsData } = useQuery({
    queryKey: ['erp-versions'],
    queryFn: async () => {
      const res = await deploymentsApi.getErpVersions()
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })
  const erpVersionOptions = (erpVersionsData && erpVersionsData.length > 0)
    ? erpVersionsData.map(v => v.name)
    : ERP_VERSION_FALLBACK

  const createDeployment = useCreateDeployment()
  const triggerDeployment = useTriggerDeployment()

  const {
    currentStep, selectedProducts, environmentConfig,
    poolTier, setPoolTier,
    nextStep, prevStep, setStep, toggleProduct, toggleProductGroup, setProductSize, setEnvironmentConfig, reset,
  } = useDeploymentWizard()
  const { workwiseSelected: hasWorkwise } = useWorkwiseSelection()

  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set())
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showServiceDeskModal, setShowServiceDeskModal] = useState(false)
  const [useBlueGreen, setUseBlueGreen] = useState(false)
  const [autoFix, setAutoFix] = useState(environmentConfig.auto_fix_enabled)
  // TEST-ONLY: skip infrastructure provisioning and trigger the Ross app deploy
  // (Azure DevOps 1522) directly. Remove once app-deploy is verified end-to-end.
  const [skipInfra, setSkipInfra] = useState(false)
  const [loadBalancerRequired, setLoadBalancerRequired] = useState(environmentConfig.load_balancer_required)
  const [dataDiskSizes, setDataDiskSizes] = useState<number[]>(environmentConfig.data_disk_sizes)
  const [additionalTags, setAdditionalTags] = useState<Record<string, string>>(environmentConfig.additional_tags)
  // Ross-specific state
  const [customerNumber, setCustomerNumber] = useState<number | undefined>(environmentConfig.customer_number)
  const [appServerCount, setAppServerCount] = useState(environmentConfig.app_server_count)
  const [createElasticPool, setCreateElasticPool] = useState(environmentConfig.create_elastic_pool)
  const [createDatabase, setCreateDatabase] = useState(environmentConfig.create_database)
  const [databaseName, setDatabaseName] = useState(environmentConfig.database_name)
  const [rossApp, setRossApp] = useState(environmentConfig.ross_app)
  const setRA = (patch: Partial<typeof rossApp>) => setRossApp((p) => ({ ...p, ...patch }))
  const [traverseForm, setTraverseForm] = useState(environmentConfig.traverse)
  const patchTraverse = (patch: Partial<typeof traverseForm>) =>
    setTraverseForm((current) => ({ ...current, ...patch }))
  // What the Application (ERP) step asks of Traverse: License management and Application creation (the rest is taken from earlier answers).
  const [applicationAnswers, setApplicationAnswers] = useState<ApplicationAnswers>(() => applicationAnswersFrom(environmentConfig.application))
  // Everything the Infrastructure flow collects (see components/infrastructure).
  const infra = useInfrastructureWizard()
  const recheckServerNames = useServerNameRecheck()
  const [customerDisplayName, setCustomerDisplayName] = useState('')
  const [bacUrl] = useState(environmentConfig.bac_url ?? '')

  const infraForm = useForm<InfraFormValues>({
    resolver: zodResolver(infraSchema),
    defaultValues: {
      pipeline_type: environmentConfig.pipeline_type,
    },
  })

  const form = useForm<EnvFormValues>({
    resolver: zodResolver(envSchema),
    defaultValues: {
      name: environmentConfig.name,
      description: environmentConfig.description,
      customer_name: environmentConfig.customer_name,
      tenant_id: environmentConfig.tenant_id,
      cloud_provider: environmentConfig.cloud_provider,
      environment: 'stg' as Environment,
      azure_region: environmentConfig.azure_region,
      secondary_region: environmentConfig.secondary_region,
      ibm_region: environmentConfig.ibm_region,
      ibm_region_secondary: environmentConfig.ibm_region_secondary,
      ibm_resource_group: environmentConfig.ibm_resource_group,
      ibm_account_id: environmentConfig.ibm_account_id,
      ibm_vpc_name: environmentConfig.ibm_vpc_name,
      strategy: environmentConfig.strategy,
    },
  })

  const selectedProvider = form.watch('cloud_provider') || environmentConfig.cloud_provider
  const selectedRegion = form.watch('azure_region') || environmentConfig.azure_region
  const { data: azurePricing, isFetching: pricingLoading } = useAzurePricing(
    selectedProvider === 'azure' ? selectedRegion : ''
  )

  // Determine whether to show blue-green option
  const showBlueGreen = selectedProducts.some((sel) =>
    BLUE_GREEN_FAMILIES.has(sel.product.family)
  )

  // Show Ross-specific fields when any selected product is Ross
  const hasRoss = selectedProducts.some((sel) => sel.product.slug === 'ross')
  const hasTraverse = selectedProducts.some((sel) => isTraverse(sel.product))
  const rossProduct = selectedProducts.find(s => s.product.slug === 'ross')
  const { data: rossDeploymentCost, isFetching: rossCosting } = useRossDeploymentCost(
    hasRoss && rossProduct && currentStep === 5
      ? {
          region: environmentConfig.azure_region || selectedRegion || 'East US',
          size: rossProduct.size,
          appServerCount: environmentConfig.app_server_count ?? 1,
          dataDiskSizes: environmentConfig.data_disk_sizes ?? [],
        }
      : null
  )

  // Region selected on the Infrastructure step drives all per-region resources.
  const watchedInfraRegion = selectedRegion
  const watchedEnv = form.watch('environment')

  // Apprise on a newly provisioned Azure VM — detected by slug OR family 'apprise'.
  const appriseSel = selectedProducts.find((sel) => isAppriseProduct(sel.product))
  const hasApprise = !!appriseSel
  const appriseResources = appriseSel
    ? sizeConfigs?.find((sc) => sc.product === appriseSel.product.id && sc.size === appriseSel.size)?.resources
    : undefined
  const appriseForm = useAppriseVmForm({
    enabled: hasApprise,
    environment: watchedEnv,
    region: selectedRegion ?? '',
    sku: appriseSel?.size ?? 'S',
    resources: appriseResources,
  })

  // Apprise VM is Azure + GitHub Actions only (contract) — lock both.
  useEffect(() => {
    if (!hasApprise) return
    infraForm.setValue('pipeline_type', 'github_actions')
    form.setValue('cloud_provider', 'azure')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasApprise])
  // Apprise is set up for the environments the server lists (Development for now; empty = no limit): offer only
  // those, and never leave a value picked for another product selected.
  const appriseEnvironments = hasApprise ? (appriseForm.options.allowed_environments ?? []) : []
  const appriseEnvironmentsKey = appriseEnvironments.join(',')
  useEffect(() => {
    if (appriseEnvironments.length === 0) return
    if (!appriseEnvironments.includes(form.getValues('environment'))) form.setValue('environment', appriseEnvironments[0] as Environment)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appriseEnvironmentsKey])
  // WorkWise: its fields live inside these same steps (one form for steps 3-5).
  const workwiseSelection = selectedProducts.find((s) => s.product.slug === 'workwise')
  const ww = useWorkwiseFlow({
    enabled: hasWorkwise,
    environment: watchedEnv,
    region: selectedRegion,
    cicd: infraForm.watch('pipeline_type'),
    appSku: workwiseSelection?.size,
    productId: workwiseSelection?.product.id,
  })
  // WorkWise offers Dev / Staging / Production on Azure only: drop a value left by another product.
  useEffect(() => {
    if (!hasWorkwise) return
    if (!(WORKWISE_ENVIRONMENTS as string[]).includes(form.getValues('environment'))) form.setValue('environment', 'stg')
    if (form.getValues('cloud_provider') !== 'azure') form.setValue('cloud_provider', 'azure')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWorkwise])
  // North Central US is offered to WorkWise only: never leave it selected for another product.
  useEffect(() => {
    if (!hasWorkwise && !hasRoss && WORKWISE_ONLY_REGIONS.includes(form.getValues('azure_region') ?? '')) {
      form.setValue('azure_region', 'East US 2')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWorkwise, hasRoss])
  // The Infrastructure flow (Environment -> Region -> Resource group -> Level -> Servers -> Add-ons) replaces the old
  // Region & Network and Database details cards for every Azure product. It configures ONE product, so Traverse or
  // Ross wins when selected, and otherwise it is the first product selected.
  const primaryProduct = selectedProducts.find((sel) => isTraverse(sel.product))?.product
    ?? rossProduct?.product
    ?? selectedProducts[0]?.product
  // A Traverse deployment is for the product line chosen in its settings, and a Process Pro one is for Process Pro Global: the
  // flow is told the line, and everything that depends on it (naming, images, the questions asked) follows. Any other product
  // (Ross, AppCentral) is as it was.
  const traverseLineId: ProductLineId = isProductLineId(traverseForm.product_line) ? traverseForm.product_line : DEFAULT_PRODUCT_LINE_ID
  const primaryLine = primaryProduct && !hasTraverse ? productLineOf(primaryProduct) : undefined
  const infraProduct: InfraProduct | null = !primaryProduct
    ? null
    : hasTraverse
      ? infraProductForLine(traverseLineId)
      : primaryLine
        ? infraProductForLine(primaryLine.id)
        : { slug: primaryProduct.slug, name: primaryProduct.name }
  // WorkWise has its own Customer / Servers / Network sections (and its own region choice), so it never uses this flow.
  // Apprise (on its own) has its own live Region and network inside its Create-VM tabs.
  const appriseOwnsRegion = hasApprise && !hasRoss && !hasTraverse
  const showInfraFlow = selectedProvider !== 'ibm' && infraProduct !== null && !hasWorkwise && !appriseOwnsRegion
  // The RDS VMs of a Dedicated RDS server (Traverse lines, Process Pro Global) are created together: the number the user chose in the
  // Servers step is the free IPs the subnet needs, the vCPU quota that is checked and the names reserved. Every other server is one VM.
  const requiredIps = rdsVmCount(infra)
  // The VM size each new server is built with in the chosen region (the catalog's, or the nearest series of the same size).
  const vmSizes = useVmSizeCheck(requiredIps)
  // The public IPs the servers asked for need the quota to be there: the same answer the Servers step shows (one shared query), so the final
  // gate agrees with the step and a payload is never built on a quota that was not confirmed.
  const publicIpQuota = usePublicIpQuota(showInfraFlow)
  const infraContext = { requiredIps, vmSizeProblems: vmSizes.problems, publicIpProblems: publicIpQuota.problems }
  const infraValid = !showInfraFlow || validateInfrastructure(infra, infraContext).valid
  const infraPayload = showInfraFlow ? buildInfrastructurePayload(infra, infraContext) : null
  const infraLegacy = legacyFields(infra)
  // Process Pro still asks for the Customer Code (the pipeline and the tags use it; no server name does). Ross and Traverse already ask for it in their own fields.
  const needsCustomerNumberInput = showInfraFlow && !hasRoss && !hasTraverse && requiresCustomerCode(infraProduct)
  // Customer ID (all environments) derives the SYS DB (SYS + ID) and Main DB (ID) names.
  const traverseCustomerIdError = validateCustomerId(traverseForm.customer_id)
  const { sysDb: traverseSysdb, mainDb: traverseMainDb } = deriveCustomerDbNames(traverseForm.customer_id)
  // The Traverse pipeline needs a resource group, VNet and subnet; the flow supplies them from whichever branch was chosen.
  const traverseNetworkKnown = infraLegacy.resourceGroup !== '' && infraLegacy.vnet !== '' && infraLegacy.subnet !== ''
  const traverseStep3Valid = hasTraverse
    && customerNumber != null && customerNumber >= 100 && customerNumber <= 999
    && customerNameProblem(traverseForm.customer_name) === null
    && traverseCustomerIdError === null
    && infraValid
    && traverseNetworkKnown
  // The Traverse block's session host count is the RDS VM count of the Infrastructure flow (1 to 8, which its validation checks): the
  // pipeline config and the Review step read it from here, so it follows that one answer.
  useEffect(() => {
    if (!hasTraverse) return
    setTraverseForm((current) => (current.session_host_count === requiredIps ? current : { ...current, session_host_count: requiredIps }))
  }, [hasTraverse, requiredIps])

  // The Application (ERP) step: Traverse asks for the TLS port, and a product line (Traverse, Process Pro) for the license of the
  // environment's type. Both moved here from the Infrastructure step. When the application can be installed follows the Infrastructure choices.
  const traverseApplication = hasTraverse && !hasRoss
  const applicationHasLicense = showInfraFlow && asksLicense(infraProduct)
  const showApplicationSettings = !hasRoss && (traverseApplication || applicationHasLicense)
  const applicationContext: ApplicationContext = {
    infra,
    customerName: traverseForm.customer_name,
    productLineId: traverseForm.product_line,
    tlsPort: traverseForm.tls_port,
    sysDb: traverseSysdb,
    mainDb: traverseMainDb,
  }
  const applicationValid =
    !showApplicationSettings || applicationProblems(infra, { traverse: traverseApplication, tlsPort: traverseForm.tls_port, answers: applicationAnswers }).length === 0

  // BACPAC list is environment-scoped and must follow the LIVE Environment
  // dropdown (watchedEnv), not the wizard-store value — Production reads the prod
  // storage account (rossinfradeployment); other environments read the test one.
  const { data: bacpacData, isFetching: bacpacLoading, isError: bacpacError, refresh: refreshBacpac } = useBacpacFiles(watchedEnv)
  const bacpacFiles = bacpacData?.files ?? []
  const rossRes = hasRoss ? getRossRegionResources(watchedEnv)[watchedInfraRegion ?? ''] : undefined
  const autoVNet = rossRes?.vnet ?? ''
  const autoSubnet = rossRes?.subnet ?? ''
  const autoRG = rossRes?.rg ?? ''
  const autoSql = rossRes?.sql ?? ''

  // Database name auto-computed: sqldb-{region}-{envShort}-ros-{erpMajor}-{customerName}
  const computedDbName = (() => {
    if (!hasRoss || !customerNumber) return ''
    const regionCode = (() => {
      const res = getRossRegionResources(watchedEnv)[watchedInfraRegion ?? '']
      return res?.location ?? (watchedInfraRegion ?? 'centralus').toLowerCase().replace(/\s+/g, '')
    })()
    const envShortMap: Record<string, string> = {
      dev: 'tst', stg: 'stg', qa: 'tst', uat: 'tst', demo: 'tst',
      production: 'prd', prod_usgov: 'prd', uat_usgov: 'tst',
    }
    const envShort = envShortMap[watchedEnv] ?? watchedEnv.slice(0, 3)
    // Prefer the admin-managed authoritative major version for the selected ERP
    // version; fall back to parsing digits out of the name (e.g. 'ERP81GA' → 81).
    const selectedErp = erpVersionsData?.find((v) => v.name === rossApp.erp_version)
    const erpMajor = selectedErp?.major_version
      ?? rossApp.erp_version.match(/ERP(\d+)/)?.[1]
      ?? rossApp.erp_version
    const custPart = (customerDisplayName || `cust${customerNumber}`).replace(/[^a-zA-Z0-9]/g, '')
    return `sqldb-${regionCode}-${envShort}-ros-${erpMajor}-${custPart}`
  })()

  // Reset wizard to step 1 every time the page mounts (fresh navigation)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reset(); useInfrastructureWizard.getState().reset() }, [])

  // Keep the ERP version selection in sync with the admin-managed options. The
  // stored default (e.g. 'ERP81GA') may not exist in the admin list, in which
  // case the <select> visually shows the first option while state still holds
  // the stale default — so the auto-computed database name (and the value sent
  // to the pipeline) used the wrong ERP major. Snap to the first real option.
  useEffect(() => {
    if (erpVersionOptions.length && !erpVersionOptions.includes(rossApp.erp_version)) {
      setRA({ erp_version: erpVersionOptions[0] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erpVersionsData])

  // The Infrastructure flow owns Environment and Region. Mirror them into the form, which the rest of the wizard
  // (the Ross BACPAC list, the Traverse environment type, the deployment request) still reads.
  const flowEnvironmentId = infra.environment?.id
  const flowRegionLabel = infra.region?.label
  useEffect(() => {
    if (showInfraFlow && flowEnvironmentId) form.setValue('environment', flowEnvironmentId as Environment)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInfraFlow, flowEnvironmentId])
  useEffect(() => {
    if (showInfraFlow && flowRegionLabel) form.setValue('azure_region', flowRegionLabel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInfraFlow, flowRegionLabel])

  // Load Balancer drives app-server count: No → locked to 1; Yes → at least 2.
  useEffect(() => {
    setAppServerCount((n) => (loadBalancerRequired ? (n < 2 ? 2 : n) : 1))
  }, [loadBalancerRequired])

  useEffect(() => {
    if (computedDbName) setDatabaseName(computedDbName)
  }, [computedDbName])

  // Sync Step 3 region selection into Step 4 form
  useEffect(() => {
    if (environmentConfig.azure_region) form.setValue('azure_region', environmentConfig.azure_region)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentConfig.azure_region])

  useEffect(() => {
    if (preselectedProvider && preselectedProvider !== environmentConfig.cloud_provider) {
      setEnvironmentConfig({ cloud_provider: preselectedProvider })
      form.setValue('cloud_provider', preselectedProvider)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedProvider])

  useEffect(() => {
    if (preselectedProductId && products) {
      const product = products.find((p) => p.id === Number(preselectedProductId))
      if (product && !selectedProducts.find((s) => s.product.id === product.id)) {
        toggleProduct(product)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedProductId, products])

  const getSizeConfig = (productId: number, size: ProductSize) =>
    sizeConfigs?.find((sc) => sc.product === productId && sc.size === size)

  // Traverse and Process Pro are sized by the RDS pool tier (the only size the user chooses; every RDS VM is the same, the DB VM is fixed);
  // every other product keeps the platform's own T-shirt size configs.
  const catalogProducts = selectedProducts.filter((sel) => usesPoolTiers(sel.product))
  const otherProducts = selectedProducts.filter((sel) => !usesPoolTiers(sel.product))
  const hasCatalogProduct = catalogProducts.length > 0
  // The DB VM line of the estimate and of the submitted sizing exists only when a new SQL VM is built.
  const newSqlVm = createsNewSqlVm(environmentConfig.infrastructure)
  // A new RDS server is built (the golden image, Trusted launch and Azure Hybrid Benefit apply to it).
  const newRdsServer = environmentConfig.infrastructure?.rdsServer.mode === 'dedicated'

  const isProdEnv = PROD_ENVS.includes(environmentConfig.environment)

  // Step 3 (Infrastructure & Settings) now carries the region, pipeline and Terraform
  // params — validate both forms, then persist everything. Name / Customer / Tenant are
  // no longer collected in the UI, so they are derived automatically here.
  const handleStep3Submit = async () => {
    // With the Infrastructure flow, Environment and Region are the flow's. Put them in the form so the saved config,
    // and everything that reads it, sees the same values.
    const envVal = showInfraFlow && infra.environment ? (infra.environment.id as Environment) : form.getValues('environment')
    if (showInfraFlow) {
      form.setValue('environment', envVal)
      form.setValue('azure_region', infraLegacy.azureRegion)
    }
    const traverseEnvType = PROD_ENVS.includes(envVal as Environment) ? 'Production' : 'Sandbox'
    if (hasRoss && customerNumber != null) {
      form.setValue('name', `ross-cust${customerNumber}-${envVal}`)
      form.setValue('customer_name', customerDisplayName || `Ross Customer ${customerNumber}`)
      form.setValue('tenant_id', `ross-${customerNumber}`)
    } else if (hasTraverse && customerNumber != null) {
      form.setValue('name', `traverse-cust${customerNumber}-${envVal}`)
      form.setValue('customer_name', traverseForm.customer_name.trim() || `Traverse ${customerNumber}`)
      form.setValue('tenant_id', `traverse-${customerNumber}`)
    } else {
      const slug = selectedProducts[0]?.product.slug ?? 'deployment'
      const sfx = Date.now().toString(36).slice(-4)
      form.setValue('name', `${slug}-${envVal}-${sfx}`)
      form.setValue('customer_name', selectedProducts[0]?.product.name ?? 'Customer')
      form.setValue('tenant_id', `${slug}-${sfx}`)
    }
    if (hasWorkwise) {
      // WorkWise: name / customer / tenant come from its Customer section.
      const wwCustomer = ww.form.getValues('customer')
      form.setValue('name', `WorkWise ${wwCustomer.number} - ${wwCustomer.name.trim()} (${envVal})`)
      form.setValue('customer_name', wwCustomer.name.trim() || 'WorkWise customer')
      form.setValue('tenant_id', wwCustomer.slug || 'workwise')
      if (!(await ww.validateStep(3))) return
    }
    const okEnv = await form.trigger()
    const okInfra = await infraForm.trigger()
    if (!okEnv || !okInfra) return
    if (hasRoss && (customerNumber == null || customerNumber < 100 || customerNumber > 999)) {
      return  // visual error already shown beside the field — block silent submission
    }
    if (hasRoss && !customerDisplayName.trim()) {
      return  // visual error shown below customer name field
    }
    // An incomplete flow, or a region with blocking issues (which the flow's validation includes), cannot be
    // deployed. The Next button is already disabled and the reasons are shown in the flow; guard here as well.
    if (!infraValid) return
    if (hasApprise && appriseForm.hasErrors) {
      return  // inline errors shown in the Azure Server Configuration section
    }
    // The generated server names are checked again just before saving: another server may have taken a number meanwhile.
    if (showInfraFlow) {
      let changed: string[]
      try {
        changed = await recheckServerNames()
      } catch {
        useInfrastructureWizard.getState().setNotice('The server names could not be checked again just now. Try again in a moment.')
        return
      }
      if (changed.length > 0) {
        useInfrastructureWizard.getState().setNotice(`${changed.join(' ')} Check the new name and continue.`)
        return
      }
    }
    if (hasRoss && !(environmentConfig.bacpac_file ?? '').trim()) {
      // The Ross infrastructure pipeline requires a BACPAC to restore the
      // database (deploy-ross-infrastructure.yml hard-fails without one), so a
      // selection is mandatory. Error shown beside the BACPAC field.
      return
    }
    if (hasTraverse && !traverseStep3Valid) return
    // For Traverse, the network and database the flow chose, in the shape its pipeline has always carried. Every other
    // product keeps the Traverse block as it was: its infrastructure is in `infrastructure` alone.
    const traverseDatabase = hasTraverse && showInfraFlow ? infraLegacy.database : undefined
    const traverseNetwork = hasTraverse
      ? { resource_group_name: infraLegacy.resourceGroup, virtual_network_name: infraLegacy.vnet, subnet_name: infraLegacy.subnet }
      : {
          resource_group_name: traverseForm.resource_group_name.trim(),
          virtual_network_name: traverseForm.virtual_network_name.trim(),
          subnet_name: traverseForm.subnet_name.trim(),
        }
    setEnvironmentConfig({
      ...(form.getValues() as Parameters<typeof setEnvironmentConfig>[0]),
      strategy: useBlueGreen ? 'blue_green' : form.getValues('strategy'),
      pipeline_type: infraForm.getValues('pipeline_type') as PipelineType,
      auto_fix_enabled: autoFix,
      create_public_ip: true,
      load_balancer_required: loadBalancerRequired,
      data_disk_sizes: dataDiskSizes,
      additional_tags: additionalTags,
      customer_number: customerNumber,
      app_server_count: appServerCount,
      create_elastic_pool: createElasticPool,
      create_database: createDatabase,
      database_name: databaseName,
      bac_url: bacUrl,
      apprise_vm: hasApprise ? appriseForm.buildPayload() : null,
      // Apprise: the generic public IP / load balancer / data disk params follow the Apprise block.
      ...(hasApprise ? appriseForm.legacyParams() : {}),
      infrastructure: infraPayload ?? undefined,
      traverse: {
        ...traverseForm,
        customer_name: traverseForm.customer_name.trim(),
        customer_id: normalizeCustomerId(traverseForm.customer_id),
        comp_id: traverseMainDb,
        sysdb: traverseSysdb,
        ...traverseNetwork,
        // The flow does not ask for an NSG; the pipeline uses the subnet's own.
        nsg_name: '',
        // With the Infrastructure flow the RDS server's own public IP answer is the only one (the old toggle is hidden), and the pipeline's legacy
        // key follows it. Without the flow, the toggle's value stands.
        create_public_ip: hasTraverse && showInfraFlow ? rdsPublicIp(infra) : traverseForm.create_public_ip,
        env_type: traverseEnvType,
        database: traverseDatabase,
      },
    })
    // Pre-populate Step 4 customer_id from customer_number if still at the default
    if (hasRoss && customerNumber != null && rossApp.customer_id === 'cid0001') {
      setRA({ customer_id: `cid${customerNumber}` })
    }
    // Pre-populate the target VM name from the infra naming convention
    // (app-server VM = PROSSSAASAP<customer_number>). This is the VM the infra
    // run provisions and whose ADO agent (customer_<vmName>) pipeline 1522 deploys
    // onto — free-typing it (e.g. "prosssaasapp154") demands a non-existent agent.
    // Left overridable for existing/dev VMs (e.g. the skip-infra test targets).
    if (hasRoss && customerNumber != null && !rossApp.vm_name.trim()) {
      setRA({ vm_name: `PROSSSAASAP${customerNumber}` })
    }
    // WorkWise with the Application (ERP) step skipped (infrastructure only): straight to Review & Deploy.
    if (hasWorkwise && ww.skipApplication) setStep(stepAfter(currentStep, true))
    else nextStep()
  }

  const executeDeployment = async (
    approvalRequired: boolean,
    approvalNotes: string,
    ticket?: { title: string; description: string; priority: string }
  ) => {
    try {
      const adminPassword = hasApprise ? appriseForm.getAdminPassword() : undefined
      const isIBM = environmentConfig.cloud_provider === 'ibm'
      // Same per-region SQL server used for the read-only "SQL Server" review
      // field (autoSql) — resolved again here so the actual FQDN reaches the
      // backend, which needs it to create the FIN/MAN database users.
      const rossSqlServerShortName = hasRoss
        ? (getRossRegionResources(environmentConfig.environment)[environmentConfig.azure_region ?? '']?.sql ?? '')
        : ''
      const rossSqlServerFqdn = rossSqlServerShortName ? `${rossSqlServerShortName}.database.windows.net` : undefined
      // The pool tier and its sessions, the RDS VM, and the DB VM only when a new SQL VM is built, each with its spec spelled out.
      const sizing = sizingForDeployment(selectedProducts, poolTier, environmentConfig.infrastructure)
      const deploymentData = {
        name: environmentConfig.name,
        description: environmentConfig.description,
        customer_name: environmentConfig.customer_name,
        tenant_id: environmentConfig.tenant_id,
        cloud_provider: environmentConfig.cloud_provider,
        environment: environmentConfig.environment,
        // Azure fields (only sent for Azure)
        ...(!isIBM && {
          azure_region: environmentConfig.azure_region,
          secondary_region: environmentConfig.secondary_region || undefined,
        }),
        // IBM Cloud fields (only sent for IBM)
        ...(isIBM && {
          ibm_region: environmentConfig.ibm_region,
          ibm_region_secondary: environmentConfig.ibm_region_secondary || undefined,
          ibm_resource_group: environmentConfig.ibm_resource_group,
          ibm_account_id: environmentConfig.ibm_account_id || undefined,
          ibm_vpc_name: environmentConfig.ibm_vpc_name || undefined,
        }),
        strategy: useBlueGreen ? ('blue_green' as DeploymentStrategy) : environmentConfig.strategy,
        approval_required: approvalRequired,
        approval_notes: approvalNotes,
        pipeline_type: environmentConfig.pipeline_type,
        pipeline_url: selectedProducts.map(s => s.product.pipeline_url).find(u => u) || undefined,
        pipeline_config: {
          create_public_ip: environmentConfig.create_public_ip,
          load_balancer_required: environmentConfig.load_balancer_required,
          data_disk_sizes: environmentConfig.data_disk_sizes,
          additional_tags: environmentConfig.additional_tags,
          // Everything the Infrastructure flow collected, as one typed object (InfrastructurePayload).
          ...(environmentConfig.infrastructure && { infrastructure: environmentConfig.infrastructure }),
          // The License Server Manager's details and the set-up script's inputs. The two database passwords are lifted out by the API.
          ...(environmentConfig.application && { application: environmentConfig.application }),
          ...(sizing && { sizing }),
          // TEST-ONLY: skip infra, trigger app deploy directly. Remove with skip box.
          ...(skipInfra && { skip_infra: true }),
          ...(hasTraverse && {
            customer_number: environmentConfig.customer_number,
            // The pipeline is sent the value it has always been sent for the line; the line's own id travels beside it.
            traverse: {
              ...environmentConfig.traverse,
              product_line: pipelineValueOf(environmentConfig.traverse.product_line),
              product_line_id: environmentConfig.traverse.product_line,
            },
          }),
          ...(hasRoss && {
            customer_number: environmentConfig.customer_number,
            app_server_count: environmentConfig.app_server_count,
            create_elastic_pool: environmentConfig.create_elastic_pool,
            create_database: environmentConfig.create_database,
            database_name: environmentConfig.database_name,
            ross_app: {
              ...environmentConfig.ross_app,
              bacpac_file: environmentConfig.bacpac_file,
              db_server: rossSqlServerFqdn,
              db_name: environmentConfig.database_name || undefined,
            },
            ...(environmentConfig.bac_url && { bac_url: environmentConfig.bac_url }),
          }),
          ...(hasApprise && environmentConfig.apprise_vm && { apprise_vm: environmentConfig.apprise_vm }),
        },
        auto_fix_enabled: environmentConfig.auto_fix_enabled,
        items: selectedProducts.map((s) => ({ product: s.product.id, size: s.size })),
        // Apprise custom password: write-only top-level field (never part of pipeline_config / the store).
        ...(hasApprise && adminPassword && { admin_password: adminPassword }),
      }
      // WorkWise has its own create endpoint (POST /workwise/deployments/); the rest is shared.
      const created = hasWorkwise
        ? await ww.create({ required: approvalRequired, notes: approvalNotes })
        : await createDeployment.mutateAsync(deploymentData)
      if (hasApprise) appriseForm.clearPassword()
      if (ticket) {
        try {
          await fetch(`/api/v1/deployments/requests/${created.id}/create_ticket/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify(ticket),
          })
        } catch { /* non-blocking */ }
      }
      if (!approvalRequired) {
        await triggerDeployment.mutateAsync(created.id)
      }
      reset()
      useInfrastructureWizard.getState().reset()
      navigate(`/deployments/${created.id}`)
    } catch {
      // error visible via mutation state
    }
  }

  const handleDeployClick = async () => {
    // WorkWise: everything (incl. the production confirmation) must be valid before the modals.
    if (hasWorkwise && !(await ww.validateAll())) return
    setShowServiceDeskModal(true)
  }

  const handleServiceDeskSubmit = (ticketData: { title: string; description: string; priority: string }) => {
    setShowServiceDeskModal(false)
    setShowApprovalModal(true)
    sessionStorage.setItem('pending_ticket', JSON.stringify(ticketData))
  }

  const handleServiceDeskSkip = () => {
    setShowServiceDeskModal(false)
    setShowApprovalModal(true)
  }

  const handleApprovalSubmit = (notes: string) => {
    setShowApprovalModal(false)
    const ticketRaw = sessionStorage.getItem('pending_ticket')
    const ticket = ticketRaw ? JSON.parse(ticketRaw) : undefined
    sessionStorage.removeItem('pending_ticket')
    executeDeployment(true, notes, ticket)
  }

  const handleDeployDirect = () => {
    setShowApprovalModal(false)
    const ticketRaw = sessionStorage.getItem('pending_ticket')
    const ticket = ticketRaw ? JSON.parse(ticketRaw) : undefined
    sessionStorage.removeItem('pending_ticket')
    executeDeployment(false, '', ticket)
  }

  const canGoNext = () => {
    if (currentStep === 1) return selectedProducts.length > 0
    if (currentStep === 3 && hasApprise && appriseForm.hasErrors) return false
    // Infrastructure & Settings step: a BACPAC is mandatory for Ross — the infra
    // pipeline restores the database from it and fails without one.
    if (currentStep === 3 && hasRoss) {
      // A BACPAC is mandatory, and the infrastructure must be complete — which includes a region with no
      // blocking issues, since a faulty region cannot advance toward Deploy.
      return infraValid && (environmentConfig.bacpac_file ?? '').trim().length > 0
    }
    // WorkWise: wait for the customer-number check; the rest is validated on Next.
    if (currentStep === 3 && hasWorkwise) {
      return ww.availability.status !== 'checking' && ww.availability.status !== 'taken'
    }
    if (currentStep === 3 && hasTraverse) return traverseStep3Valid
    if (currentStep === 3) return infraValid
    // Application (ERP) step: pipeline 1522 rejects empty tenantId/coid with a 400,
    // and an empty vmName makes it fall back to a placeholder VM with no ADO agent
    // ("No agent found"). Require all three before advancing for Ross deployments.
    if (currentStep === 4 && hasRoss) {
      return (
        rossApp.tenant_id.trim().length > 0 &&
        rossApp.coid.trim().length > 0 &&
        rossApp.vm_name.trim().length > 0
      )
    }
    if (currentStep === 4) return applicationValid
    return true
  }

  const handleNextClick = async () => {
    if (currentStep === 3) {
      handleStep3Submit()
    } else if (currentStep === 4) {
      if (hasWorkwise && !(await ww.validateStep(4))) return
      // Application (ERP) step — persist the Ross app-deploy params, or the settings entered here (Traverse TLS port, the license),
      // and when the application can be installed, then advance.
      setEnvironmentConfig({
        ross_app: rossApp,
        ...(showApplicationSettings && {
          // The license is part of the infrastructure payload: it is complete only now, so the payload is saved again with it.
          infrastructure: infraPayload ?? undefined,
          application_install: applicationInstallPlan(infra).timing,
          ...(traverseApplication && {
            traverse: { ...environmentConfig.traverse, tls_port: traverseForm.tls_port.trim() },
            application: buildApplicationConfig(applicationContext, applicationAnswers),
          }),
        }),
      })
      nextStep()
    } else {
      nextStep()
    }
  }

  const workwiseSkipsApplication = hasWorkwise && ww.skipApplication

  const deployError = createDeployment.error ?? triggerDeployment.error
  const isDeploying = createDeployment.isPending || triggerDeployment.isPending || ww.creating

  // Product lines are costed by the size catalog (see SizingCostSummary), so they are not in this total.
  const totalEstimatedCost = otherProducts.reduce((sum, sel) => {
    // Apprise VM is priced separately (AppriseCostSummary) — never guessed here.
    if (isAppriseProduct(sel.product)) return sum
    const cfg = getSizeConfig(sel.product.id, sel.size)
    const azurePrice = azurePricing?.[sel.size]?.monthly_estimate
    const vmPrice = azurePrice || cfg?.resources.estimated_monthly_cost_usd || 0
    const replicas = cfg?.resources.min_replicas ?? 3
    const vmTotal = vmPrice * replicas
    const ipCost = Math.round(replicas * 3.65)
    return sum + vmTotal + ipCost
  }, 0)

  const effectiveStrategy = useBlueGreen ? 'blue_green' : environmentConfig.strategy

  return (
    <div className={styles.page}>
      {/* Modals */}
      {showServiceDeskModal && (
        <ServiceDeskModal
          deploymentName={environmentConfig.name}
          onSubmit={handleServiceDeskSubmit}
          onSkip={handleServiceDeskSkip}
        />
      )}
      {showApprovalModal && (
        <ApprovalModal
          isProd={isProdEnv}
          onApprove={handleApprovalSubmit}
          onDirect={handleDeployDirect}
          onCancel={() => setShowApprovalModal(false)}
        />
      )}

      {/* Step Indicator */}
      <div className={styles.stepIndicator}>
        {STEPS.map((step, idx) => {
          const isCompleted = currentStep > step.number
          const isActive = currentStep === step.number
          const Icon = step.icon
          return (
            <div key={step.number} className={styles.stepGroup}>
              <div className={clsx(styles.step, isActive && styles.stepActive, isCompleted && styles.stepCompleted)}>
                <div className={styles.stepCircle}>
                  {isCompleted ? <Check size={14} /> : <Icon size={14} />}
                </div>
                <div className={styles.stepInfo}>
                  <span className={styles.stepNum}>Step {step.number}</span>
                  <span className={styles.stepLabel}>{step.label}</span>
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={clsx(styles.connector, isCompleted && styles.connectorDone)} />
              )}
            </div>
          )
        })}
      </div>

      {/* Step Content */}
      <div className={styles.content}>

        {currentStep > 1 && (currentStep !== 3 || !showInfraFlow) && infraProduct && (
          <div style={{ marginBottom: 12 }}>
            <ProductChip product={infraProduct} />
          </div>
        )}

        {/* ── Step 1: Products ── */}
        {currentStep === 1 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Choose Products</h2>
            <p className={styles.stepDesc}>Select the products to deploy. Products with microservices can be expanded to pick individual components.</p>
            <WorkwiseSelectionNotice />

            {(bundles ?? []).length > 0 && (
              <div className={styles.bundleSection}>
                <h3 className={styles.sectionTitle}>Quick Bundles</h3>
                <div className={styles.bundleGrid}>
                  {(bundles ?? []).map((bundle) => (
                    <button
                      key={bundle.id}
                      className={styles.bundleCard}
                      onClick={() => {
                        const bundleProds = (products ?? []).filter((p) => (bundle.products ?? []).includes(p.id))
                        bundleProds.forEach((p) => {
                          if (!selectedProducts.find((s) => s.product.id === p.id)) toggleProduct(p)
                        })
                      }}
                    >
                      <Rocket size={18} className={styles.bundleIcon} />
                      <span className={styles.bundleName}>{bundle.name}</span>
                      <span className={styles.bundleDesc}>{bundle.description}</span>
                      <span className={styles.bundleProducts}>{(bundle.product_names ?? []).join(', ')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Product Catalog</h3>
              {selectedProducts.length > 0 && (
                <span className={styles.selectedBadge}>{selectedProducts.length} component{selectedProducts.length !== 1 ? 's' : ''} selected</span>
              )}
            </div>

            {(productsLoading || rolesLoading || !rolesInitialized) ? (
              <LoadingSpinner label="Loading products..." />
            ) : (() => {
              const visibleProducts = (products ?? []).filter(
                (p) => p.parent === null && (isAdmin || isDeployer || canDeploy(p.slug))
              )
              if (visibleProducts.length === 0) {
                return (
                  <EmptyState
                    icon={<Package size={36} />}
                    title="No products available"
                    description="You do not have access to any products. Contact your platform admin to be added to a team."
                  />
                )
              }
              return (
                <ProductList
                  products={visibleProducts}
                  allProducts={products ?? []}
                  selectedProducts={selectedProducts}
                  expandedProducts={expandedProducts}
                  onToggleExpand={(id) => setExpandedProducts((prev) => {
                    const next = new Set(prev)
                    if (next.has(id)) { next.delete(id) } else { next.add(id) }
                    return next
                  })}
                  onToggleProduct={toggleProduct}
                  onToggleGroup={toggleProductGroup}
                />
              )
            })()}
          </div>
        )}

        {/* ── Step 2: Sizes ── */}
        {currentStep === 2 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Configure Sizes</h2>
            <p className={styles.stepDesc}>
              Choose the RDS pool tier for Traverse and Process Pro, and the T-shirt size for every other product.
              {pricingLoading && <span className={styles.pricingNote}><RefreshCw size={11} className={styles.spin} /> Fetching Azure live pricing…</span>}
            </p>

            {hasCatalogProduct && (
              <div className={styles.sizeSection} data-testid="app-size-section">
                <div className={styles.sizeSectionHeader}>
                  <Package size={16} className={styles.sizeProductIcon} />
                  <span className={styles.sizeProductName}>{catalogProducts.map((sel) => sel.product.name).join(', ')}</span>
                </div>
                <PoolTierSelector
                  name="pool-tier"
                  legend="RDS pool tier"
                  value={poolTier}
                  onChange={setPoolTier}
                  hint="The tier sets how many sessions the RDSH pool takes. Every tier is built on the same VM, and the database VM is a fixed size, so this is the only size you choose. A tier is not the Dedicated or Existing choice of a server."
                />
              </div>
            )}

            {otherProducts.map((sel) => {
              const hasConfigs = SIZES.some((s) => getSizeConfig(sel.product.id, s) !== undefined)
              return (
                <div key={sel.product.id} className={styles.sizeSection}>
                  <div className={styles.sizeSectionHeader}>
                    <Package size={16} className={styles.sizeProductIcon} />
                    <span className={styles.sizeProductName}>{sel.product.name}</span>
                  </div>
                  {!hasConfigs ? (
                    <div className={styles.emptySizes}>
                      <AlertCircle size={16} />
                      <span>No T-shirt sizes configured for this product. Go to <strong>Admin Settings → Platform Config</strong> to add size configurations.</span>
                    </div>
                  ) : sel.product.slug === 'workwise' ? (
                    <WorkwiseSizeGrid adminSizes={ww.adminSizes} sizePrices={ww.sizePrices} pricesStatus={ww.sizePricesStatus} selected={tshirtOf(sel.size, 'M')} onSelect={(size) => setProductSize(sel.product.id, size)} />
                  ) : (
                    <div className={styles.sizeGrid}>
                      {SIZES.map((size) => {
                        const config = getSizeConfig(sel.product.id, size)
                        const azurePrice = azurePricing?.[size]
                        const isSelected = sel.size === size
                        if (!config) return null
                        return (
                          <button
                            key={size}
                            className={clsx(styles.sizeCard, isSelected && styles.sizeCardSelected)}
                            disabled={isAppriseProduct(sel.product) && !config.resources.vm}
                            title={isAppriseProduct(sel.product) && !config.resources.vm ? 'No Azure VM size is available for this size' : undefined}
                            onClick={() => setProductSize(sel.product.id, size)}
                          >
                            <div className={styles.sizeCardTop}>
                              <SizeBadge size={size} />
                              <span className={styles.sizeName}>{SIZE_LABELS[size]}</span>
                              {isSelected && <Check size={14} className={styles.sizeCheck} />}
                            </div>
                            {isAppriseProduct(sel.product) ? (
                              <AppriseSizeDetails vm={config.resources.vm ?? null} sizeLabel={SIZE_LABELS[size]}
                                entered={{ cpu: config.resources.cpu_request, memory: config.resources.memory_request }}
                                userCount={config.resources.max_users ?? null} />
                            ) : (
                            <div className={styles.sizeDetails}>
                              {config.resources.db_tier && (
                                <div className={styles.sizeDetail}>
                                  <span>Users</span>
                                  <span>{config.resources.db_tier}</span>
                                </div>
                              )}
                              <div className={styles.sizeDetail}>
                                <span>CPU</span>
                                <span>{config.resources.cpu_request} vCPU</span>
                              </div>
                              <div className={styles.sizeDetail}>
                                <span>RAM</span>
                                <span>{config.resources.memory_request}</span>
                              </div>
                              {!config.resources.aks && (azurePrice?.sku || config.resources.storage_class) && (
                                <div className={styles.sizeDetail}>
                                  <span>SKU</span>
                                  <span className={styles.skuTag}>{azurePrice?.sku ?? config.resources.storage_class}</span>
                                </div>
                              )}
                              <div className={styles.sizeDetail}>
                                <span>Replicas</span>
                                <span>{config.resources.min_replicas}</span>
                              </div>
                              {config.resources.aks && (
                                <>
                                  <div className={styles.sizeDetail}>
                                    <span>AKS node VM</span>
                                    <span className={styles.skuTag}>{config.resources.aks.node_vm_size}</span>
                                  </div>
                                  <div className={styles.sizeDetail}>
                                    <span>Nodes</span>
                                    <span>
                                      {config.resources.aks.autoscaling_enabled
                                        ? `${config.resources.aks.node_min_count}–${config.resources.aks.node_max_count}`
                                        : config.resources.aks.node_count}
                                    </span>
                                  </div>
                                  <div className={styles.sizeDetail}>
                                    <span>OS disk</span>
                                    <span>{config.resources.aks.node_os_disk_gb} GB</span>
                                  </div>
                                </>
                              )}
                            </div>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {isAppriseProduct(sel.product) && (
                    <AppriseUnverifiedNotice fallback={hasConfigs && !SIZES.some((s) => !!getSizeConfig(sel.product.id, s)?.resources.vm)} />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── Step 3: Infrastructure & Settings ── */}
        {currentStep === 3 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Infrastructure &amp; Settings</h2>
            <p className={styles.stepDesc}>Deployment details, target region, CI/CD pipeline and infrastructure parameters.</p>

            <form id="infra-form" className={styles.form} onSubmit={(e) => { e.preventDefault(); handleStep3Submit() }}>

              {/* WorkWise: Customer section first */}
              {hasWorkwise && <WorkwiseCustomerSection flow={ww} />}

              {/* Environment */}
              <div className={styles.networkSection}>
                <h4 className={styles.networkSectionTitle}>Environment</h4>
                <div className={styles.formGrid}>
                  {showInfraFlow ? (
                    <div className={styles.formGroup}>
                      <span className={styles.label}>Environment</span>
                      <div className={styles.vnetInfoBox} data-testid="environment-from-flow">
                        {infra.environment
                          ? <span className={styles.vnetName}>{infra.environment.label}</span>
                          : <span className={styles.vnetHint}>Not chosen yet</span>}
                        <span className={styles.vnetHint}>Chosen in the Infrastructure section below</span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Environment <span className={styles.required}>*</span></label>
                      <select {...form.register('environment')} className={styles.select}>
                        {ENVIRONMENTS
                          .filter((env) => !hasWorkwise || (WORKWISE_ENVIRONMENTS as string[]).includes(env.value))
                          .filter((env) => appriseEnvironments.length === 0 || appriseEnvironments.includes(env.value))
                          .map((env) => (
                          <option key={env.value} value={env.value}>{env.label}{env.gov ? ' 🛡' : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Cloud Provider <span className={styles.required}>*</span></label>
                    <select {...form.register('cloud_provider')} className={styles.select}>
                      {(hasWorkwise ? CLOUD_PROVIDERS.filter((cp) => cp.value === 'azure') : CLOUD_PROVIDERS).map((cp) => <option key={cp.value} value={cp.value} disabled={hasApprise && cp.value !== 'azure'}>{cp.label}</option>)}
                    </select>
                    {hasApprise && <div className={styles.fieldHint}>Apprise is provisioned as an Azure Virtual Machine</div>}
                  </div>
                </div>

                {/* Ross: Customer + ERP Version in the Environment section */}
                {hasRoss && (
                  <>
                    <div className={styles.formGrid} style={{ marginTop: 16 }}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>{CUSTOMER_CODE_LABEL} <span className={styles.required}>*</span></label>
                        <input
                          type="number" min={100} max={999}
                          aria-label={CUSTOMER_CODE_LABEL}
                          className={clsx(styles.input, (customerNumber == null || customerNumber < 100 || customerNumber > 999) && styles.inputError)}
                          value={customerNumber ?? ''}
                          onChange={(e) => setCustomerNumber(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                          placeholder="e.g. 109"
                        />
                        <div className={styles.fieldHint}>Ross customer identifier (100–999)</div>
                        {(customerNumber == null || customerNumber < 100 || customerNumber > 999) && (
                          <span className={styles.errorMsg}>
                            {customerNumber == null ? `${CUSTOMER_CODE_LABEL} is required for Ross` : 'Must be between 100 and 999'}
                          </span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Customer Name <span className={styles.required}>*</span></label>
                        <input
                          type="text" className={styles.input}
                          value={customerDisplayName}
                          onChange={(e) => setCustomerDisplayName(e.target.value)}
                          placeholder="e.g. Hammond"
                        />
                        <div className={styles.fieldHint}>Short name used in database name and deployment label</div>
                        {!customerDisplayName.trim() && (
                          <span className={styles.errorMsg}>Customer name is required</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.formGroup} style={{ marginTop: 16 }}>
                      <label className={styles.label}>ERP Version <span className={styles.required}>*</span></label>
                      <select
                        className={styles.select}
                        value={rossApp.erp_version}
                        onChange={(e) => setRA({ erp_version: e.target.value })}
                      >
                        {erpVersionOptions.map((v: string) => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <div className={styles.fieldHint}>Ross ERP version to install via Azure DevOps pipeline</div>
                    </div>

                    {/* BACPAC file picker — live from Azure Storage */}
                    <div className={styles.formGroup} style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <label className={styles.label} style={{ marginBottom: 0 }}>BACPAC File</label>
                        <button
                          type="button"
                          onClick={refreshBacpac}
                          disabled={bacpacLoading}
                          style={{ background: 'none', border: 'none', cursor: bacpacLoading ? 'not-allowed' : 'pointer', color: 'var(--aptean-teal)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px' }}
                        >
                          <RefreshCw size={12} style={{ animation: bacpacLoading ? 'spin 0.8s linear infinite' : 'none' }} />
                          {bacpacLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </div>
                      <select
                        className={styles.select}
                        value={environmentConfig.bacpac_file}
                        onChange={(e) => setEnvironmentConfig({ bacpac_file: e.target.value })}
                        disabled={bacpacLoading}
                      >
                        <option value="">— Select a BACPAC file —</option>
                        {bacpacFiles.map((f) => (
                          <option key={f.name} value={f.name}>
                            {f.name}{f.size_mb ? ` · ${f.size_mb} MB` : ''}{f.last_modified ? ` · ${f.last_modified.slice(0, 10)}` : ''}
                          </option>
                        ))}
                      </select>
                      {hasRoss && !(environmentConfig.bacpac_file ?? '').trim() ? (
                        <span className={styles.errorMsg}>
                          A BACPAC file is required — the Ross infrastructure pipeline restores the database from it.
                        </span>
                      ) : (
                        <div className={styles.fieldHint}>
                          {bacpacLoading ? 'Loading files from Azure Storage…' :
                            bacpacError ? 'Could not reach storage — check ROSS_BACPAC_STORAGE_KEY secret' :
                            bacpacData?.stale ? 'Showing cached list — click Refresh for latest' :
                            bacpacData?.error ? `Could not reach storage: ${bacpacData.error}` :
                            `${bacpacFiles.length} file${bacpacFiles.length !== 1 ? 's' : ''} from ${bacpacData?.source === 'prod' ? 'Production' : 'Test'} storage${bacpacData?.account ? ` (${bacpacData.account})` : ''} · auto-refreshed hourly`}
                        </div>
                      )}
                    </div>

                    <div className={styles.formGroup} style={{ marginTop: 16 }}>
                      <label className={styles.label}>Database Name <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--aptean-text-muted)' }}>(auto-computed)</span></label>
                      <div className={styles.vnetInfoBox}>
                        {computedDbName ? (
                          <>
                            <span className={styles.vnetName} style={{ fontFamily: 'monospace', fontSize: 13 }}>{computedDbName}</span>
                            <span className={styles.vnetHint}>Derived from region · environment · ERP version · customer name</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--aptean-text-muted)' }}>{'sqldb-{region}-{env}-ros-{erp_version}-{customer_name}'}</span>
                            <span className={styles.vnetHint}>Fill in {CUSTOMER_CODE_LABEL} and Customer Name above to see the computed name</span>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {needsCustomerNumberInput && (
                  <div className={styles.formGrid} style={{ marginTop: 16 }}>
                    <div className={styles.formGroup}>
                      <label className={styles.label} htmlFor="customer-number">{CUSTOMER_CODE_LABEL} <span className={styles.required}>*</span></label>
                      <input
                        id="customer-number"
                        type="number" min={100} max={999}
                        className={clsx(styles.input, customerNumber != null && (customerNumber < 100 || customerNumber > 999) && styles.inputError)}
                        value={customerNumber ?? ''}
                        onChange={(e) => setCustomerNumber(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                        placeholder="e.g. 101"
                      />
                      <div className={styles.fieldHint}>Identifies the customer in the pipeline and its tags (100–999). Server names do not use it.</div>
                      {customerNumber != null && (customerNumber < 100 || customerNumber > 999) && (
                        <span className={styles.errorMsg}>Must be between 100 and 999</span>
                      )}
                    </div>
                  </div>
                )}

                {hasTraverse && (
                  <div className={styles.formGrid} style={{ marginTop: 16 }}>
                    <div className={styles.formGroup}>
                      <label className={styles.label} htmlFor="traverse-customer-code">{CUSTOMER_CODE_LABEL} <span className={styles.required}>*</span></label>
                      <input
                        id="traverse-customer-code"
                        type="number" min={100} max={999}
                        className={clsx(styles.input, (customerNumber == null || customerNumber < 100 || customerNumber > 999) && styles.inputError)}
                        value={customerNumber ?? ''}
                        onChange={(e) => setCustomerNumber(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                        placeholder="e.g. 101"
                      />
                      <div className={styles.fieldHint}>Identifies the customer in the pipeline and its tags (100–999). Server names do not use it.</div>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label} htmlFor="traverse-customer-name">Customer Name <span className={styles.required}>*</span></label>
                      <input
                        id="traverse-customer-name"
                        className={styles.input}
                        value={traverseForm.customer_name}
                        aria-required="true"
                        onChange={(e) => patchTraverse({ customer_name: e.target.value })}
                        placeholder="ACME01"
                      />
                      <div className={styles.fieldHint}>This becomes the AD group “&lt;name&gt; Remote Users”.</div>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="traverse-product-line" className={styles.label}>Product Line <span className={styles.required}>*</span></label>
                      <select
                        id="traverse-product-line"
                        className={styles.select}
                        value={traverseLineId}
                        onChange={(e) => {
                          const product_line = e.target.value as ProductLineId
                          patchTraverse({ product_line })
                          // Shared at once, not only when the step is saved, so every later step reads the same line.
                          setEnvironmentConfig({ traverse: { ...environmentConfig.traverse, product_line } })
                        }}
                      >
                        {PRODUCT_LINES.map((line) => <option key={line.id} value={line.id}>{line.label}</option>)}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label} htmlFor="traverse-customer-id">Customer ID <span className={styles.required}>*</span></label>
                      <input
                        id="traverse-customer-id"
                        className={clsx(styles.input, traverseCustomerIdError !== null && styles.inputError)}
                        value={traverseForm.customer_id}
                        maxLength={3}
                        aria-required="true"
                        aria-invalid={traverseCustomerIdError !== null ? true : undefined}
                        aria-describedby="traverse-customer-id-note"
                        onChange={(e) => patchTraverse({ customer_id: normalizeCustomerId(e.target.value) })}
                        placeholder="AGR"
                      />
                      {traverseCustomerIdError !== null && traverseForm.customer_id !== ''
                        ? <span role="alert" className={styles.errorMsg}>{traverseCustomerIdError}</span>
                        : null}
                      <div id="traverse-customer-id-note" className={styles.fieldHint}>3 letters or digits, e.g. AGR for Agrabase. Saved in uppercase.</div>
                    </div>
                  </div>
                )}

                {PROD_ENVS.includes(form.watch('environment') as Environment) && (
                  <div className={styles.approvalNotice}>
                    <ShieldCheck size={14} />
                    Production environments require manager approval before deployment executes.
                  </div>
                )}
              </div>

              {/* Region, network and database: the Infrastructure flow, for every Azure product (Apprise: its Create-VM tabs) */}
              {appriseOwnsRegion ? null : selectedProvider !== 'ibm' ? (
                hasWorkwise ? (
                  <div className={styles.networkSection}>
                    <h4 className={styles.networkSectionTitle}>Region &amp; Network</h4>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Azure Region <span className={styles.required}>*</span></label>
                        <select className={styles.select} {...form.register('azure_region')}>
                          {WORKWISE_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : infraProduct && (
                  <>
                    {selectedProducts.length > 1 && (
                      <div className={styles.fieldHint} data-testid="infra-primary-product-note">
                        Several products are selected. The infrastructure choices below are for {infraProduct.name}.
                      </div>
                    )}
                    <InfrastructureFlow product={infraProduct} customerNumber={customerNumber ?? null} requiredIps={requiredIps} />
                    {hasTraverse && infraValid && !traverseNetworkKnown && (
                      <div role="alert" className={styles.lbInfoBanner} style={{ borderColor: 'var(--aptean-red, #b91c1c)' }}>
                        <AlertCircle size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
                        The chosen servers have no VNet and subnet on record, which the Traverse pipeline needs. Choose a different server, or a new environment.
                      </div>
                    )}
                  </>
                )
              ) : (
                <div className={styles.networkSection}>
                  <h4 className={styles.networkSectionTitle}>IBM Cloud Region</h4>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>IBM Region <span className={styles.required}>*</span></label>
                      <select {...form.register('ibm_region')} className={styles.select}>
                        {IBM_REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Resource Group</label>
                      <input {...form.register('ibm_resource_group')} className={styles.input} placeholder="default" />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>IBM Account ID</label>
                      <input {...form.register('ibm_account_id')} className={styles.input} placeholder="IBM Cloud account ID" />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>VPC Name</label>
                      <input {...form.register('ibm_vpc_name')} className={styles.input} placeholder="aptean-vpc-prod" />
                    </div>
                  </div>
                </div>
              )}

              {/* Apprise: Azure Server Configuration */}
              {hasApprise && selectedProvider !== 'ibm' && (
                <AppriseServerSection
                  form={appriseForm}
                  sizeLabel={SIZE_LABELS[tshirtOf(appriseSel?.size, 'S')]}
                  onRegionChange={(r) => form.setValue('azure_region', r, { shouldDirty: true })}
                />
              )}

              {/* CI/CD Pipeline */}
              <div className={styles.formGroup}>
                <label className={styles.label}>CI/CD Pipeline</label>
                <div className={styles.radioGroup}>
                  <label className={clsx(styles.radioOption, infraForm.watch('pipeline_type') === 'github_actions' && styles.radioOptionSelected)}>
                    <input type="radio" value="github_actions" {...infraForm.register('pipeline_type')} />
                    <Github size={16} />
                    GitHub Actions
                  </label>
                  <label className={clsx(styles.radioOption, infraForm.watch('pipeline_type') === 'azure_devops' && styles.radioOptionSelected)}>
                    <input type="radio" value="azure_devops" disabled={hasApprise} {...infraForm.register('pipeline_type')} />
                    <Cloud size={16} />
                    Azure DevOps
                  </label>
                </div>
                {hasApprise && (
                  <div className={styles.fieldHint}>Apprise VM deployments currently support GitHub Actions only.</div>
                )}
              </div>

              {/* Blue-green section — only shown when AppCentral or IAM products are selected */}
              {showBlueGreen && (
                <div className={styles.blueGreenSection}>
                  <div className={styles.toggleRow}>
                    <div>
                      <span className={styles.toggleLabel}>Blue-Green Deployment</span>
                      <span className={styles.toggleDesc}>Deploy to green slot, switch traffic when ready</span>
                    </div>
                    <Toggle checked={useBlueGreen} onChange={setUseBlueGreen} />
                  </div>
                </div>
              )}

              {/* WorkWise: App / Database / Gateway servers, tags and a summary replace Pipeline Parameters and Auto-Fix */}
              {hasWorkwise ? <WorkwiseInfraSections flow={ww} /> : (<>
              {/* Pipeline / Terraform parameters (Apprise: LB, disks and tags live in its Create-VM tabs) */}
              {(!hasApprise || hasRoss) && (
              <div className={styles.networkSection}>
                <h4 className={styles.networkSectionTitle}>Pipeline Parameters</h4>
                <p style={{ margin: '0 0 16px', fontSize: '0.8125rem', color: 'var(--aptean-text-secondary)' }}>
                  Forwarded to the Terraform pipeline on trigger.
                </p>

                {/* With the Infrastructure flow each dedicated server is asked its own public IP question in the Servers step, so the old
                    toggle (one answer for every session host) is not shown. */}
                {hasTraverse && !hasRoss && !showInfraFlow && (
                  <div className={styles.toggleRow} style={{ marginBottom: 20 }}>
                    <div>
                      <span className={styles.toggleLabel}>Public IP</span>
                      <span className={styles.toggleDesc}>Attach a Standard public IP to each session host. Leave off for a private subnet.</span>
                    </div>
                    <Toggle
                      checked={traverseForm.create_public_ip}
                      onChange={(value) => patchTraverse({ create_public_ip: value })}
                    />
                  </div>
                )}

                {/* Load Balancer */}
                {!hasTraverse && !hasApprise && (
                <div className={styles.formGroup} style={{ marginBottom: 20 }}>
                  <label className={styles.label}>Load Balancer Required</label>
                  <div className={styles.radioGroup}>
                    <label className={clsx(styles.radioOption, !loadBalancerRequired && styles.radioOptionSelected)}>
                      <input type="radio" name="lb_required" checked={!loadBalancerRequired}
                        onChange={() => setLoadBalancerRequired(false)} />
                      No
                    </label>
                    <label className={clsx(styles.radioOption, loadBalancerRequired && styles.radioOptionSelected)}>
                      <input type="radio" name="lb_required" checked={loadBalancerRequired}
                        onChange={() => setLoadBalancerRequired(true)} />
                      Yes
                    </label>
                  </div>
                  {loadBalancerRequired && (
                    <div className={styles.lbInfoBanner}>
                      <strong>2 app servers</strong> will be provisioned with identical configuration and fronted by an
                      <strong> Azure Application Gateway WAF v2</strong> for web-application firewall protection and SSL offload.
                    </div>
                  )}
                </div>
                )}

                {/* Ross: App Server Count — locked to 1 unless a Load Balancer is required */}
                {hasRoss && (
                  <div className={styles.formGroup} style={{ marginBottom: 20 }}>
                    <label className={styles.label}>App Server Count</label>
                    <input
                      type="number"
                      min={loadBalancerRequired ? 2 : 1}
                      max={8}
                      className={styles.input}
                      value={appServerCount}
                      disabled={!loadBalancerRequired}
                      onChange={(e) => setAppServerCount(Math.max(2, Math.min(8, parseInt(e.target.value, 10) || 2)))}
                    />
                    <div className={styles.fieldHint}>
                      {loadBalancerRequired
                        ? 'Number of application servers to provision (2–8, load-balanced).'
                        : 'Locked to 1 — set Load Balancer Required to “Yes” to provision multiple app servers.'}
                    </div>
                  </div>
                )}

                {/* Ross: Create Elastic Pool */}
                {hasRoss && (
                  <div style={{ marginBottom: 20 }}>
                    <div className={styles.toggleRow}>
                      <div>
                        <span className={styles.toggleLabel}>Create Elastic Pool</span>
                        <span className={styles.toggleDesc}>Provision an Azure SQL Elastic Pool for this customer</span>
                      </div>
                      <Toggle checked={createElasticPool} onChange={setCreateElasticPool} />
                    </div>
                    {!createElasticPool && createDatabase && (
                      <span className={styles.fieldHint} style={{ display: 'block', marginTop: 6 }}>
                        Elastic pool is not being created. Once the database is created, add it to an
                        elastic pool — the database uses the Business Critical tier.
                      </span>
                    )}
                  </div>
                )}

                {/* Ross: Create Database — independent of the pool toggle. When the
                    pool isn't created here, the DB is added to an elastic pool as a
                    follow-up step (the database itself is Business Critical). */}
                {hasRoss && (
                  <div className={styles.toggleRow} style={{ marginBottom: 20 }}>
                    <div>
                      <span className={styles.toggleLabel}>Create Database</span>
                      <span className={styles.toggleDesc}>
                        {createElasticPool
                          ? 'Provision a SQL Database inside the Elastic Pool'
                          : 'Provision a Business Critical SQL Database — add it to an elastic pool afterward'}
                      </span>
                    </div>
                    <Toggle checked={createDatabase} onChange={setCreateDatabase} />
                  </div>
                )}
                {/* Data Disks. A Traverse deployment with the Infrastructure page chooses its RDS server's disks there (optional), so this list is not asked. Apprise has none. */}
                {!hasApprise && !(hasTraverse && showInfraFlow) && (
                <div className={styles.formGroup}>
                  <label className={styles.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Data Disks</span>
                    <button type="button" className={styles.addTagBtn}
                      onClick={() => setDataDiskSizes(d => [...d, 256])}>
                      + Add Disk
                    </button>
                  </label>
                  {dataDiskSizes.length === 0 ? (
                    <div className={styles.emptyHint}>
                      {hasTraverse
                        ? 'No extra disks listed. The Traverse pipeline adds one 128 GB Premium disk (disk-01) when this stays empty.'
                        : 'No data disks configured'}
                    </div>
                  ) : (
                    <div className={styles.diskList}>
                      {dataDiskSizes.map((gb, i) => (
                        <div key={i} className={styles.diskRow}>
                          <input
                            type="number" min={32} max={32768} step={32}
                            className={styles.diskInput}
                            value={gb}
                            onChange={e => {
                              const v = parseInt(e.target.value, 10)
                              setDataDiskSizes(d => d.map((x, idx) => idx === i ? (isNaN(v) ? x : v) : x))
                            }}
                          />
                          <span className={styles.diskUnit}>GB</span>
                          <button type="button" className={styles.removeBtn}
                            onClick={() => setDataDiskSizes(d => d.filter((_, idx) => idx !== i))}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Additional Tags */}
                {!hasApprise && (
                <div className={styles.formGroup} style={{ marginTop: 16 }}>
                  <label className={styles.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Additional Tags</span>
                    <button type="button" className={styles.addTagBtn}
                      onClick={() => setAdditionalTags(t => ({ ...t, '': '' }))}>
                      + Add Tag
                    </button>
                  </label>
                  {Object.keys(additionalTags).length === 0 ? (
                    <div className={styles.emptyHint}>No tags — click Add Tag to label resources</div>
                  ) : (
                    <div className={styles.tagList}>
                      {Object.entries(additionalTags).map(([k, v], i) => (
                        <div key={i} className={styles.tagRow}>
                          <input className={styles.tagKeyInput} placeholder="key" value={k}
                            onChange={e => {
                              const newKey = e.target.value
                              setAdditionalTags(t => {
                                const entries = Object.entries(t)
                                entries[i] = [newKey, v]
                                return Object.fromEntries(entries)
                              })
                            }} />
                          <span className={styles.tagSep}>=</span>
                          <input className={styles.tagValInput} placeholder="value" value={v}
                            onChange={e => {
                              const newVal = e.target.value
                              setAdditionalTags(t => {
                                const entries = Object.entries(t)
                                entries[i] = [k, newVal]
                                return Object.fromEntries(entries)
                              })
                            }} />
                          <button type="button" className={styles.removeBtn}
                            onClick={() => setAdditionalTags(t => {
                              const entries = Object.entries(t).filter((_, idx) => idx !== i)
                              return Object.fromEntries(entries)
                            })}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>
              )}

              {/* Claude AI auto-fix toggle */}
              <div className={styles.autoFixSection}>
                <div className={styles.toggleRow}>
                  <div>
                    <span className={styles.toggleLabel}>
                      <Sparkles size={14} style={{ display: 'inline', marginRight: 5, color: 'var(--aptean-teal)' }} />
                      Claude AI Auto-Fix
                    </span>
                    <span className={styles.toggleDesc}>Automatically diagnose and fix deployment errors</span>
                  </div>
                  <Toggle checked={autoFix} onChange={setAutoFix} />
                </div>
              </div>
              </>)}

            </form>
          </div>
        )}

        {/* ── Step 4: Application (ERP) — deployed via Azure DevOps after infra ── */}
        {currentStep === 4 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Application (ERP)</h2>
            {hasWorkwise ? (
              <p className={styles.stepDesc}>
                WorkWise is installed on the app server after the infrastructure run. Credentials are Key Vault references only.
              </p>
            ) : (
            <p className={styles.stepDesc}>
              {traverseApplication
                ? 'The application settings, and when the application can be installed.'
                : applicationHasLicense && !hasRoss
                  ? 'The license of this environment, and when the application can be installed.'
                  : <>Ross ERP is installed via <strong>Azure DevOps</strong> onto the VM provisioned by the infrastructure run, in the <strong>same region</strong> selected on the Infrastructure step{watchedInfraRegion ? ` (${watchedInfraRegion})` : ''}.</>}
            </p>
            )}

            {showApplicationSettings ? (
              <div className={styles.form}>
                <ApplicationSettings
                  traverse={traverseApplication}
                  tlsPort={traverseForm.tls_port}
                  onTlsPortChange={(tls_port) => patchTraverse({ tls_port })}
                  sysDb={traverseSysdb}
                  mainDb={traverseMainDb}
                  answers={applicationAnswers}
                  onAnswersChange={setApplicationAnswers}
                  customerName={traverseForm.customer_name}
                  productLineId={traverseForm.product_line}
                />
                {traverseApplication && (
                  <div className={styles.networkSection}>
                    <h4 className={styles.networkSectionTitle}>Traverse environment</h4>
                    <div className={styles.reviewTable}>
                      <ReviewRow label="Customer" value={environmentConfig.traverse.customer_name || traverseForm.customer_name || '—'} />
                      <ReviewRow label="Product line" value={productLineLabel(environmentConfig.traverse.product_line)} />
                      <ReviewRow label="Customer ID" value={environmentConfig.traverse.customer_id || '—'} mono />
                      <ReviewRow label="Resource group" value={environmentConfig.traverse.resource_group_name || '—'} mono />
                      <ReviewRow label="VNet" value={environmentConfig.traverse.virtual_network_name || '—'} mono />
                      <ReviewRow label="Subnet" value={environmentConfig.traverse.subnet_name || '—'} mono />
                    </div>
                  </div>
                )}
              </div>
            ) : hasRoss ? (
              <div className={styles.form}>
                <div className={styles.networkSection}>
                  <h4 className={styles.networkSectionTitle}>Ross Application Parameters (Azure DevOps)</h4>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Tenant ID <span className={styles.required}>*</span></label>
                      <input className={clsx(styles.input, !rossApp.tenant_id.trim() && styles.inputError)} placeholder="appcentral tenant id" required
                        value={rossApp.tenant_id} onChange={(e) => setRA({ tenant_id: e.target.value })} />
                      <div className={styles.fieldHint}>Required by pipeline 1522 (AppCentral instance).</div>
                      {!rossApp.tenant_id.trim() && (
                        <span className={styles.errorMsg}>Tenant ID is required — pipeline 1522 rejects an empty value.</span>
                      )}
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>COID <span className={styles.required}>*</span></label>
                      <input className={clsx(styles.input, !rossApp.coid.trim() && styles.inputError)} placeholder="appcentral coid" required
                        value={rossApp.coid} onChange={(e) => setRA({ coid: e.target.value })} />
                      <div className={styles.fieldHint}>Required by pipeline 1522 (AppCentral instance).</div>
                      {!rossApp.coid.trim() && (
                        <span className={styles.errorMsg}>COID is required — pipeline 1522 rejects an empty value.</span>
                      )}
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Customer Domain</label>
                      <input className={styles.input} placeholder="https://customer.rosserp.apteancloud.com"
                        value={rossApp.customer_domain} onChange={(e) => setRA({ customer_domain: e.target.value })} />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Target VM Name <span className={styles.required}>*</span></label>
                      <input className={clsx(styles.input, !rossApp.vm_name.trim() && styles.inputError)} placeholder="e.g. drosssaasac001" required
                        value={rossApp.vm_name} onChange={(e) => setRA({ vm_name: e.target.value })} />
                      <div className={styles.fieldHint}>Must match a VM whose ADO agent (customer_&lt;vmName&gt;) is online in RossReleasePool — provisioned by the infra run. Leaving it blank makes pipeline 1522 fall back to a placeholder with no agent.</div>
                      {!rossApp.vm_name.trim() && (
                        <span className={styles.errorMsg}>Target VM name is required — an empty value makes the ADO run fail with “No agent found”.</span>
                      )}
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>IAF Version</label>
                      <select className={styles.select} value={rossApp.iaf_version} onChange={(e) => setRA({ iaf_version: e.target.value })}>
                        {IAF_VERSIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>SharePoint / DocConnect URL</label>
                      <input className={styles.input} placeholder="https://docconnect1.rosserp.apteancloud.com:20100/"
                        value={rossApp.sp_url} onChange={(e) => setRA({ sp_url: e.target.value })} />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Customer ID</label>
                      <input className={styles.input} placeholder="cid0001"
                        value={rossApp.customer_id} onChange={(e) => setRA({ customer_id: e.target.value })} />
                    </div>
                  </div>
                  <div className={styles.toggleRow} style={{ marginTop: 16 }}>
                    <div>
                      <span className={styles.toggleLabel}>Install Crystal Reports (CFB)</span>
                      <span className={styles.toggleDesc}>Install the Crystal Report components</span>
                    </div>
                    <Toggle checked={rossApp.install_cfb} onChange={(v) => setRA({ install_cfb: v })} />
                  </div>
                  <div className={styles.toggleRow} style={{ marginTop: 12 }}>
                    <div>
                      <span className={styles.toggleLabel}>Install Smart Client Framework (SCF)</span>
                      <span className={styles.toggleDesc}>Install the Smart Client Framework and packages</span>
                    </div>
                    <Toggle checked={rossApp.install_scf} onChange={(v) => setRA({ install_scf: v })} />
                  </div>
                </div>
              </div>
            ) : hasWorkwise ? (
              <WorkwiseAppSettings flow={ww} />
            ) : (
              <div className={styles.networkSection}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--aptean-text-secondary)' }}>
                  No application-level configuration is required for the selected product(s).
                  Click <strong>Next</strong> to continue.
                </p>
                {hasApprise && (
                  <p style={{ margin: '10px 0 0', fontSize: '0.875rem', color: 'var(--aptean-text-secondary)' }}>
                    Apprise: the application installer is not configured. This deployment provisions the Azure
                    virtual machine only — it does not install Apprise.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Step 5: Review ── */}
        {currentStep === 5 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Review &amp; Deploy</h2>
            <p className={styles.stepDesc}>Review your configuration before submitting.</p>

            <div className={styles.reviewGrid}>
              <div className={styles.reviewSection}>
                <h3 className={styles.reviewSectionTitle}>Deployment Details</h3>
                <div className={styles.reviewTable}>
                  <ReviewRow label="Name" value={environmentConfig.name} />
                  {environmentConfig.description && <ReviewRow label="Description" value={environmentConfig.description} />}
                  <ReviewRow label="Customer" value={environmentConfig.customer_name} />
                  <ReviewRow label="Tenant ID" value={environmentConfig.tenant_id} mono />
                  <ReviewRow label="Environment" value={ENVIRONMENTS.find(e => e.value === environmentConfig.environment)?.label ?? environmentConfig.environment} />
                  <ReviewRow label="Cloud Provider" value={environmentConfig.cloud_provider === 'ibm' ? 'IBM Cloud' : 'Microsoft Azure'} />
                  {environmentConfig.cloud_provider === 'ibm' ? (
                    <>
                      <ReviewRow label="IBM Region" value={IBM_REGIONS.find(r => r.value === environmentConfig.ibm_region)?.label ?? environmentConfig.ibm_region} />
                      {environmentConfig.ibm_region_secondary && <ReviewRow label="Secondary Region" value={IBM_REGIONS.find(r => r.value === environmentConfig.ibm_region_secondary)?.label ?? environmentConfig.ibm_region_secondary} />}
                      {environmentConfig.ibm_resource_group && <ReviewRow label="Resource Group" value={environmentConfig.ibm_resource_group} />}
                      {environmentConfig.ibm_vpc_name && <ReviewRow label="VPC Name" value={environmentConfig.ibm_vpc_name} />}
                    </>
                  ) : (
                    <>
                      <ReviewRow label="Azure Region" value={environmentConfig.azure_region} />
                      {environmentConfig.secondary_region && <ReviewRow label="Secondary Region" value={environmentConfig.secondary_region} />}
                    </>
                  )}
                  <ReviewRow label="Strategy" value={STRATEGY_LABELS[effectiveStrategy as DeploymentStrategy] ?? effectiveStrategy} />
                  <ReviewRow label="Pipeline" value={environmentConfig.pipeline_type === 'github_actions' ? 'GitHub Actions' : 'Azure DevOps'} />
                </div>

                {!hasWorkwise && (
                <div className={styles.reviewTable} style={{ marginTop: 14 }}>
                  <ReviewRow label="Blue-Green" value={useBlueGreen ? 'Enabled' : 'Disabled'} />
                  <ReviewRow label="Claude AI Auto-Fix" value={environmentConfig.auto_fix_enabled ? 'Enabled' : 'Disabled'} />
                </div>
                )}

                {/* Ross-specific pipeline parameters */}
                {hasTraverse && (
                  <div className={styles.reviewTable} style={{ marginTop: 14 }}>
                    <ReviewRow label="Workflow" value="deploy-traverse-infrastructure.yml" mono />
                    <ReviewRow label={CUSTOMER_CODE_LABEL} value={environmentConfig.customer_number != null ? String(environmentConfig.customer_number) : '—'} mono />
                    <ReviewRow label="Customer Name" value={environmentConfig.traverse.customer_name || '—'} />
                    <ReviewRow label="Product Line" value={productLineLabel(environmentConfig.traverse.product_line)} />
                    <ReviewRow label="Customer ID" value={environmentConfig.traverse.customer_id || '—'} mono />
                    <ReviewRow label="SYS DB" value={environmentConfig.traverse.sysdb || '—'} mono />
                    <ReviewRow label="Main DB" value={environmentConfig.traverse.comp_id || '—'} mono />
                    <ReviewRow label="TLS Port" value={environmentConfig.traverse.tls_port} mono />
                    <ReviewRow label="Session Hosts" value={String(environmentConfig.traverse.session_host_count)} />
                    {/* With the Infrastructure page, each server's public IP answer is in its Servers section below. */}
                    {!showInfraFlow && <ReviewRow label="Public IP" value={environmentConfig.traverse.create_public_ip ? 'Yes' : 'No'} />}
                    {/* The RDS server's data disks are in the Servers section below (optional: "None" when there are none). Without the
                        Infrastructure page, the deployment's own list, one 128 GB disk when empty. */}
                    {!showInfraFlow && (
                      <ReviewRow
                        label="Data Disks"
                        value={(environmentConfig.data_disk_sizes.length > 0 ? environmentConfig.data_disk_sizes : [128])
                          .map((s, i) => `disk-${String(i + 1).padStart(2, '0')}: ${s} GB`).join(', ')}
                      />
                    )}
                  </div>
                )}

                {/* The Application (ERP) step: License management and Application creation. No password is shown. */}
                {hasTraverse && environmentConfig.application && (
                  <div className={styles.reviewTable} style={{ marginTop: 14 }} data-testid="application-review">
                    {summariseApplication(environmentConfig.application).map((row) => (
                      <ReviewRow key={row.label} label={row.label} value={row.value} mono={row.mono} />
                    ))}
                  </div>
                )}
                {hasRoss && (
                  <div className={styles.reviewTable} style={{ marginTop: 14 }}>
                    <ReviewRow label={CUSTOMER_CODE_LABEL} value={environmentConfig.customer_number != null ? String(environmentConfig.customer_number) : '—'} mono />
                    <ReviewRow label="App Server Count" value={String(environmentConfig.app_server_count)} />
                    <ReviewRow label="Create Elastic Pool" value={environmentConfig.create_elastic_pool ? 'Yes' : 'No'} />
                    <ReviewRow label="Create Database" value={environmentConfig.create_database ? 'Yes' : 'No'} />
                    {environmentConfig.create_database && <ReviewRow label="Database Name" value={environmentConfig.database_name || '—'} mono />}
                    {autoRG && <ReviewRow label="Resource Group" value={autoRG} mono />}
                    {autoVNet && <ReviewRow label="VNet" value={autoVNet} mono />}
                    {autoSubnet && <ReviewRow label="Subnet" value={autoSubnet} mono />}
                    {autoSql && <ReviewRow label="SQL Server" value={autoSql} mono />}
                    {environmentConfig.data_disk_sizes.length > 0 && (
                      <ReviewRow
                        label="Data Disks"
                        value={environmentConfig.data_disk_sizes.map((s, i) => `disk-${String(i + 1).padStart(2, '0')}: ${s} GB`).join(', ')}
                      />
                    )}
                  </div>
                )}

                {showInfraFlow && (
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Which VM series will really be built: the nearest of the same size where one is not available. */}
                    <VmSizeBanner requiredIps={requiredIps} />
                    <InfrastructureReview
                      payload={environmentConfig.infrastructure ?? null}
                      vmSizes={vmSizes.sizes}
                      onEdit={(step) => {
                        // The license is on the Application (ERP) step, after the Infrastructure step.
                        if (step === 'application') {
                          setStep(4)
                          return
                        }
                        infra.setSubStep(step)
                        setStep(3)
                      }}
                    />
                  </div>
                )}

                {isProdEnv && (
                  <div className={styles.approvalNotice}>
                    <ShieldCheck size={14} />
                    Approval required for production deployments.
                  </div>
                )}
              </div>

              <div className={styles.reviewSection}>
                <h3 className={styles.reviewSectionTitle}>Products ({selectedProducts.length})</h3>
                <div className={styles.reviewProducts}>
                  {selectedProducts.map((sel) => {
                    const config = getSizeConfig(sel.product.id, sel.size)
                    const catalogTier = usesPoolTiers(sel.product) && !isTShirtSize(sel.size) ? getPoolTier(sel.size) : null
                    return (
                      <div key={sel.product.id} className={styles.reviewProduct}>
                        <div className={styles.reviewProductHeader}>
                          <Package size={14} />
                          <span className={styles.reviewProductName}>{sel.product.name}</span>
                          {catalogTier ? <span className={styles.sizeName}>{tierHeadline(catalogTier)}</span> : isTShirtSize(sel.size) && <SizeBadge size={sel.size} />}
                        </div>
                        {catalogTier && (
                          <div className={styles.reviewProductDetails}>
                            <span>{catalogTier.label}</span>
                            <span>RDS pool tier: the sizes of its VMs are under Sizes you chose</span>
                          </div>
                        )}
                        {config && isAppriseProduct(sel.product) && (
                          <div className={styles.reviewProductDetails}>
                            <span>Azure VM: {environmentConfig.apprise_vm?.server.vm_size ?? catalogVmSizing({ resources: config.resources }).vm_size}</span>
                            <span>OS disk: {osDiskLabel(environmentConfig.apprise_vm ? environmentConfig.apprise_vm.storage.os_disk_gb : catalogVmSizing({ resources: config.resources }).os_disk_gb)}</span>
                          </div>
                        )}
                        {!catalogTier && config && !isAppriseProduct(sel.product) && sel.product.slug !== 'workwise' && (
                          <div className={styles.reviewProductDetails}>
                            <span>CPU: {config.resources.cpu_request} / {config.resources.cpu_limit}</span>
                            <span>Memory: {config.resources.memory_request}</span>
                            <span>Replicas: {config.resources.min_replicas}–{config.resources.max_replicas}</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* ── Live Azure cost breakdown (ROSS only) ── */}
                {hasRoss && (
                  <div style={{ marginTop: 16 }}>
                    {rossCosting ? (
                      <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--aptean-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid var(--aptean-teal)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        Fetching live Azure prices…
                      </div>
                    ) : rossDeploymentCost ? (
                      <div style={{ background: 'var(--aptean-bg-elevated)', border: '1px solid var(--aptean-border)', borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--aptean-border)', fontSize: 11, fontWeight: 700, color: 'var(--aptean-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                          Azure Cost Breakdown
                          <span style={{ background: 'rgba(20,184,166,0.15)', color: 'var(--aptean-teal)', padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>Live</span>
                          <span style={{ marginLeft: 'auto', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{rossDeploymentCost.region} · {rossDeploymentCost.vm_sku} · {rossDeploymentCost.total_vms} VMs</span>
                        </div>
                        {[
                          { label: `Windows VMs ×${rossDeploymentCost.breakdown.vms.count} (${rossDeploymentCost.app_server_count} App + EMF + RDS)`, value: rossDeploymentCost.breakdown.vms.total_monthly },
                          { label: `OS Disks — P10 128GB ×${rossDeploymentCost.breakdown.os_disks.count}`, value: rossDeploymentCost.breakdown.os_disks.total_monthly },
                          ...(rossDeploymentCost.breakdown.data_disks.total_monthly > 0
                            ? [{ label: `Data Disks — ${rossDeploymentCost.breakdown.data_disks.disks.map(d => `${d.gb}GB (${d.tier})`).join(', ')} ×${rossDeploymentCost.breakdown.data_disks.count}`, value: rossDeploymentCost.breakdown.data_disks.total_monthly }]
                            : []),
                          { label: `Public IPs — Standard ×${rossDeploymentCost.breakdown.public_ips.count}`, value: rossDeploymentCost.breakdown.public_ips.total_monthly },
                          ...(rossDeploymentCost.breakdown.load_balancer ? [{ label: 'Load Balancer — Standard', value: rossDeploymentCost.breakdown.load_balancer.monthly }] : []),
                          ...(rossDeploymentCost.breakdown.app_gateway ? [{ label: 'Application Gateway — Standard v2', value: rossDeploymentCost.breakdown.app_gateway.monthly }] : []),
                        ].map((row, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 14px', borderBottom: '1px solid var(--aptean-border-muted)', fontSize: 13 }}>
                            <span style={{ color: 'var(--aptean-text-secondary)' }}>{row.label}</span>
                            <span style={{ fontWeight: 600, fontFamily: 'var(--aptean-font-mono)' }}>${row.value.toFixed(2)}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--aptean-bg-base)', fontSize: 14, fontWeight: 700 }}>
                          <span>Estimated Total</span>
                          <span style={{ color: 'var(--aptean-teal)', fontSize: 16 }}>${rossDeploymentCost.total_monthly.toFixed(2)}/month</span>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.costSummary}>
                        <span className={styles.costLabel}>Estimated Total</span>
                        <span className={styles.costValue}>{formatCurrency(totalEstimatedCost)}/month</span>
                      </div>
                    )}
                  </div>
                )}
                {hasCatalogProduct && (
                  <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <SizesYouChose poolTier={poolTier} newSqlVm={newSqlVm} newRdsServer={newRdsServer} vmSizes={vmSizes.sizes} />
                    <SizingCostSummary poolTier={poolTier} newSqlVm={newSqlVm} vmSizes={vmSizes.sizes} />
                  </div>
                )}
                {!hasRoss && hasApprise && environmentConfig.apprise_vm && (
                  <AppriseCostSummary
                    config={environmentConfig.apprise_vm}
                    region={environmentConfig.azure_region}
                    otherProductsCost={totalEstimatedCost}
                  />
                )}
                {!hasRoss && !hasApprise && otherProducts.length > 0 && (hasWorkwise ? <WorkwiseCostSummary flow={ww} /> : (
                  <div className={styles.costSummary}>
                    <span className={styles.costLabel}>Estimated Total</span>
                    <span className={styles.costValue}>{formatCurrency(totalEstimatedCost)}/month</span>
                  </div>
                ))}
              </div>
              {hasWorkwise && <WorkwiseReviewSections flow={ww} />}
            </div>

            {hasApprise && environmentConfig.apprise_vm && (
              <AppriseReviewSection config={environmentConfig.apprise_vm} region={environmentConfig.azure_region} />
            )}

            {/* TEST-ONLY: skip infrastructure and trigger the app deploy (ADO 1522)
                directly. Remove this block once app-deploy is verified. */}
            {hasRoss && (
              <label
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16,
                  padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  border: '1px dashed var(--aptean-border)',
                  background: skipInfra ? 'rgba(234,179,8,0.10)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={skipInfra}
                  onChange={(e) => setSkipInfra(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ fontSize: 13, color: 'var(--aptean-text-secondary)' }}>
                  <strong>Skip infrastructure (test)</strong> — bypass Terraform provisioning
                  and trigger the application deploy (Azure DevOps 1522) directly. For
                  verifying the app-deploy path only; disable for real deployments.
                </span>
              </label>
            )}

            {deployError && (
              <div className={styles.deployError}>
                <AlertCircle size={16} />
                <span>Failed: {formatApiError(deployError)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className={styles.navButtons}>
        <Button
          variant="secondary"
          icon={<ChevronLeft size={16} />}
          onClick={() => {
            if (currentStep === 1) navigate('/deployments')
            // Review goes back to step 3 while the Application (ERP) step is skipped.
            else if (workwiseSkipsApplication) setStep(stepBefore(currentStep, true))
            else prevStep()
          }}
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>
        {currentStep < 5 && (
          <Button
            disabled={!canGoNext()}
            onClick={handleNextClick}
          >
            Next <ChevronRight size={16} />
          </Button>
        )}
        {currentStep === 5 && (
          <Button loading={isDeploying} icon={<Rocket size={16} />} onClick={handleDeployClick}>
            Deploy
          </Button>
        )}
      </div>
    </div>
  )
}

/** A product's size as a T-shirt size: what it is for the products that have one, else the fallback (a product line's size is a pool tier). */
const tshirtOf = (size: ProductSize | undefined, fallback: TShirtSize): TShirtSize => (size && isTShirtSize(size) ? size : fallback)

// ─── Product List (Step 1) ────────────────────────────────────────────────────

interface ProductListProps {
  products: Product[]
  allProducts: Product[]
  selectedProducts: Array<{ product: Product; size: ProductSize }>
  expandedProducts: Set<number>
  onToggleExpand: (id: number) => void
  onToggleProduct: (product: Product) => void
  onToggleGroup: (parent: Product, children: Product[]) => void
}

function ProductList({
  products, allProducts, selectedProducts, expandedProducts,
  onToggleExpand, onToggleProduct, onToggleGroup,
}: ProductListProps) {
  return (
    <div className={styles.productList}>
      {/* Header row */}
      <div className={styles.productListHeader}>
        <span className={styles.plColNo}>#</span>
        <span className={styles.plColName}>Product</span>
        <span className={styles.plColMicro}>Microservices</span>
        <span className={styles.plColAction}>Select</span>
      </div>

      {products.map((product, idx) => {
        const children = allProducts.filter((p) => p.parent === product.id)
        const hasChildren = children.length > 0
        const isExpanded = expandedProducts.has(product.id)

        const deployTargets = hasChildren ? children : [product]
        const allChecked = deployTargets.every((t) => selectedProducts.some((s) => s.product.id === t.id))
        const anyChecked = deployTargets.some((t) => selectedProducts.some((s) => s.product.id === t.id))

        return (
          <div key={product.id} className={styles.productListGroup}>
            {/* Main row */}
            <div className={clsx(styles.productRow, anyChecked && styles.productRowSelected)}>
              <span className={styles.plColNo}>{idx + 1}</span>
              <div className={styles.plColName}>
                <div className={styles.productRowName}>
                  <Package size={15} className={styles.productRowIcon} />
                  {product.name}
                </div>
                <span className={styles.productRowFamily}>{product.name}</span>
              </div>
              <div className={styles.plColMicro}>
                {hasChildren ? (
                  <button
                    className={styles.expandBtn}
                    onClick={() => onToggleExpand(product.id)}
                    type="button"
                  >
                    <Cpu size={12} />
                    {children.length} microservice{children.length !== 1 ? 's' : ''}
                    <ChevronDown size={12} className={clsx(styles.expandIcon, isExpanded && styles.expandIconOpen)} />
                  </button>
                ) : (
                  <span className={styles.noMicro}>—</span>
                )}
              </div>
              <div className={styles.plColAction}>
                <button
                  type="button"
                  className={clsx(styles.checkBtn, allChecked && styles.checkBtnChecked, anyChecked && !allChecked && styles.checkBtnPartial)}
                  onClick={() => onToggleGroup(product, children)}
                  aria-label={allChecked ? `Deselect ${product.name}` : `Select ${product.name}`}
                >
                  {allChecked ? <Check size={13} /> : anyChecked ? <span className={styles.partialDot} /> : null}
                </button>
              </div>
            </div>

            {/* Microservice rows */}
            {hasChildren && isExpanded && (
              <div className={styles.microserviceRows}>
                {children.map((child) => {
                  const isChildSelected = selectedProducts.some((s) => s.product.id === child.id)
                  return (
                    <div key={child.id} className={clsx(styles.microserviceRow, isChildSelected && styles.microserviceRowSelected)}>
                      <span className={styles.plColNo} />
                      <div className={styles.plColName}>
                        <div className={styles.microserviceName}>
                          <span className={styles.microserviceIndent} />
                          <Cpu size={12} className={styles.microserviceIcon} />
                          {child.name}
                        </div>
                      </div>
                      <div className={styles.plColMicro} />
                      <div className={styles.plColAction}>
                        <button
                          type="button"
                          className={clsx(styles.checkBtn, isChildSelected && styles.checkBtnChecked)}
                          onClick={() => onToggleProduct(child)}
                          aria-label={isChildSelected ? `Deselect ${child.name}` : `Select ${child.name}`}
                        >
                          {isChildSelected && <Check size={13} />}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ReviewRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.reviewRow}>
      <span className={styles.reviewLabel}>{label}</span>
      <span className={clsx(styles.reviewValue, mono && styles.reviewValueMono)}>{value}</span>
    </div>
  )
}
